/* eslint-env browser */

import type { FormEvent, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download } from "lucide-react";
import writeExcelFile, {
  type Cell as ExcelCell,
  type CellObject as ExcelCellObject,
  type Feature as ExcelFeature,
  type SheetData,
} from "write-excel-file/browser";
import { MultiSelectDropdown } from "@/components/ui/MultiSelectDropdown";
import { useAuth } from "@/lib/AuthContext";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";
import { useBrand } from "@/lib/BrandContext";
import { supabase } from "@/lib/supabaseClient";
import {
  buildSectionErrorMessage,
  formatStatsMonthLabel,
  formatCurrency,
  formatDecimal,
  formatDurationHours,
  formatInteger,
  formatPercent,
  getCurrentStatsMonthRangeKey,
  getStatsMonthOptions,
  getStatsRangeDescription,
  isStatsMonthRangeKey,
  normalizeBpiMonthlyActivityOverview,
  normalizeBpiOverview,
  PIPELINE_STATUS_ORDER,
  STATS_DATA_START_DATE,
  STATS_RANGE_OPTIONS,
} from "@/lib/statsDashboard";
import { buildStatsServiceNoteFilename } from "@/lib/statsServiceNote";
import { STATS_TARGETS } from "@/lib/statsDashboardTargets";
import type {
  StatsBpiMonthlyActivityOverview,
  StatsBpiMonthlyActivityRow,
  StatsBpiOverview,
  StatsBpiMonthlyRow,
  StatsBoardCenterRow,
  StatsBoardComparisonMetric,
  StatsBoardMetricBase,
  StatsFunnelStep,
  StatsParticipantBand,
  StatsPipeline,
  StatsRangeKey,
  StatsServiceNoteNote,
  StatsSeasonalityCell,
} from "@/lib/statsDashboardTypes";
import { useStatsDashboard } from "@/lib/useStatsDashboard";
import { useStatsServiceNote } from "@/lib/useStatsServiceNote";

const CRM_START_LABEL = STATS_DATA_START_DATE.toLocaleDateString("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const STATUS_COLORS: Record<string, string> = {
  "Non traité": "#2563eb",
  "Standby": "#7c3aed",
  "Devis envoyé": "#0f766e",
  "À relancer": "#ca8a04",
  "Déjà relancé": "#e11d48",
  Signé: "#059669",
  "Acompte reçu": "#ea580c",
  "Versement complet": "#475569",
};

const BOARD_VOLUME_SERIES = [
  { key: "incomingRequests", label: "Demandes reçues", color: "#2563eb" },
  { key: "signedRequests", label: "Passées en signé", color: "#059669" },
] as const;

const CENTER_METRICS = [
  { key: "incomingRequests", label: "Demandes reçues" },
  { key: "signedRequests", label: "Passées en signé" },
  { key: "transformationRate", label: "Transformation" },
  { key: "signedBudgetTotal", label: "CA HT événement" },
] as const;

const BPI_REVENUE_OBJECTIVE_METRIC = "b2b_revenue_ht";
const BPI_CSV_SEPARATOR = ";";
const BPI_CSV_HEADERS = [
  "Mois",
  "Centre",
  "CA HT Smile Event",
  "CA HT global centre",
  "% CA Smile Event",
  "Nb événements",
  "Demandes ASSO",
  "Demandes Entreprises / CSE",
  "Demandes",
  "Devis envoyés",
  "Devis signés",
  "Taux de transformation",
  "Objectif CA",
  "Écart objectif",
] as const;
const BPI_EXCEL_SUMMARY_HEADERS = [
  "Centre",
  "CA HT (€)",
  "CA HT global centre (€)",
  "Objectif CA (€)",
  "Écart (€)",
  "Devis envoyés",
  "Devis signés",
  "Taux de transfo.",
  "Total demandes",
] as const;

type CenterMetricKey = (typeof CENTER_METRICS)[number]["key"];
type BpiAnnualMetricKey = (typeof BPI_ANNUAL_METRICS)[number]["key"];
type BpiAnnualMetricKind = (typeof BPI_ANNUAL_METRICS)[number]["kind"];
type UserRole = "sales" | "sales_manager" | "admin";
type DeltaTone = "positive" | "negative" | "neutral";
type DeltaBadge = { label: string; detail: string; tone: DeltaTone; hidden?: boolean };
type BpiAnnualMonthOption = {
  value: string;
  label: string;
};
type BpiAnnualMonthPair = {
  currentMonth: Date;
  previousMonth: Date;
  monthLabel: string;
  currentMonthLabel: string;
  previousMonthLabel: string;
};
type BpiAnnualRange = {
  startMonth: Date;
  endMonth: Date;
  fetchFrom: Date;
  fetchTo: Date;
  selectedLabel: string;
  previousLabel: string;
  currentLegend: string;
  previousLegend: string;
  months: BpiAnnualMonthPair[];
};
type BpiAnnualDataState = {
  loading: boolean;
  error: string | null;
  overview: StatsBpiOverview;
  activityOverview: StatsBpiMonthlyActivityOverview;
};
type BpiAnnualChartRow = {
  monthLabel: string;
  currentMonthLabel: string;
  previousMonthLabel: string;
  currentValue: number | null;
  previousValue: number | null;
  currentDisplayValue: string;
  previousDisplayValue: string;
};
type BpiAnnualTotals = {
  current: number | null;
  previous: number | null;
  deltaLabel: string;
  deltaTone: DeltaTone;
  deltaDetail: string;
};
type BpiAnnualComparisonExportData = {
  metric: (typeof BPI_ANNUAL_METRICS)[number];
  range: BpiAnnualRange;
  chartRows: BpiAnnualChartRow[];
  totals: BpiAnnualTotals;
  dataAvailabilityMessage: string | null;
};
type BpiOverviewExportRow = {
  month: string;
  center: string;
  revenueHt: number | null;
  totalRevenueHt: number | null;
  revenueShare: number | null;
  confirmedEventsCount: number;
  nonB2bRequests: number;
  b2bRequests: number;
  totalRequests: number;
  quotedCases: number;
  convertedQuotedCases: number;
  conversionRate: number | null;
  targetValue: number | null;
  gap: number | null;
};

type BpiOverviewSummaryExportRow = {
  center: string;
  revenueHt: number | null;
  targetValue: number | null;
  gap: number | null;
  quotedCases: number;
  convertedQuotedCases: number;
  conversionRate: number | null;
  totalRequests: number;
};

type BpiTotalRevenueSummary = {
  value: number | null;
  complete: boolean;
  editableRow: StatsBpiMonthlyRow | null;
};
type BpiView = "summary" | "comparison";

type BpiExcelCellStyle = Omit<ExcelCellObject, "value" | "type" | "format">;

const BPI_PERIOD_TOTAL_MONTH_START = "__period_total__";
const BPI_EXCEL_MONEY_FORMAT = '#,##0 "€"';
const BPI_EXCEL_PERCENT_FORMAT = "0.0%";
const BPI_EXCEL_INTEGER_FORMAT = "0";
const BPI_EXCEL_HEADER_ROW = 4;
const BPI_EXCEL_DARK_BLUE = "#1E3A5F";
const BPI_EXCEL_LIGHT_BLUE = "#DBEAFE";
const BPI_EXCEL_MANUAL_FILL = "#FEF3C7";
const BPI_EXCEL_BORDER = "#CBD5E1";
const BPI_EXCEL_HEADER_STYLE = {
  fontWeight: "bold" as const,
  textColor: "#FFFFFF",
  backgroundColor: BPI_EXCEL_DARK_BLUE,
  borderColor: BPI_EXCEL_BORDER,
  borderStyle: "thin" as const,
  align: "center" as const,
  alignVertical: "center" as const,
  wrap: true,
  height: 28,
};
const BPI_EXCEL_TOTAL_STYLE = {
  fontWeight: "bold" as const,
  backgroundColor: BPI_EXCEL_LIGHT_BLUE,
  borderColor: BPI_EXCEL_BORDER,
  borderStyle: "thin" as const,
};
const BPI_EXCEL_EVEN_ROW_STYLE = {
  backgroundColor: "#F9FAFB",
};
const BPI_EXCEL_BASE_ROW_STYLE = {
  borderColor: BPI_EXCEL_BORDER,
  borderStyle: "thin" as const,
  alignVertical: "center" as const,
};
const BPI_EXCEL_MANUAL_STYLE = {
  backgroundColor: BPI_EXCEL_MANUAL_FILL,
};
const BPI_EXCEL_UNAVAILABLE_STYLE = {
  fontStyle: "italic" as const,
  textColor: "#9CA3AF",
};
const BPI_ANNUAL_CLIENT_TYPE = "Une entreprise ou un CSE";
const BPI_ANNUAL_PREVIOUS_COLOR = "#94A3B8";
const BPI_ANNUAL_CURRENT_COLOR = "#2563EB";
const BPI_ANNUAL_METRICS = [
  { key: "revenueHt", label: "CA HT Smile Event", kind: "currency" },
  { key: "quotedCases", label: "Devis envoyés", kind: "integer" },
  { key: "convertedQuotedCases", label: "Devis signés", kind: "integer" },
  { key: "totalRequests", label: "Total demandes", kind: "integer" },
  { key: "confirmedEventsCount", label: "Nombre d’événements", kind: "integer" },
  { key: "conversionRate", label: "Taux de transformation", kind: "percent" },
] as const;

function sameStringArray(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function pluralize(count: number, singular: string, plural?: string): string {
  return count > 1 ? (plural ?? `${singular}s`) : singular;
}

function normalizeUserRole(role: unknown): UserRole | null {
  return role === "sales" || role === "sales_manager" || role === "admin" ? role : null;
}

function canManageBpiObjectives(role: UserRole | null): boolean {
  return role === "sales_manager" || role === "admin";
}

function canEditBpiTotalRevenue(role: UserRole | null): boolean {
  return role === "sales_manager" || role === "admin";
}

function formatSignedInteger(value: number | null): string {
  if (value === null) return "N/A";
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${formatInteger(value)}`;
}

function formatSignedCurrency(value: number | null): string {
  if (value === null) return "N/A";
  const prefix = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${prefix}${formatCurrency(Math.abs(value))}`;
}

function formatPercentPoints(value: number | null): string {
  if (value === null) return "N/A";
  return `${(value * 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} pts`;
}

function comparisonTone(deltaAbsolute: number | null, positiveDirection: "up" | "down"): DeltaTone {
  if (deltaAbsolute === null || deltaAbsolute === 0) return "neutral";
  const isPositive = positiveDirection === "up" ? deltaAbsolute > 0 : deltaAbsolute < 0;
  return isPositive ? "positive" : "negative";
}

function buildCountDelta(
  metric: StatsBoardComparisonMetric,
  positiveDirection: "up" | "down" = "up",
): DeltaBadge {
  if (metric.previous === null || metric.deltaAbsolute === null) {
    return {
      label: "",
      detail: "",
      tone: "neutral",
      hidden: true,
    };
  }

  if (metric.deltaAbsolute === 0) {
    return {
      label: "Stable vs période précédente",
      detail: "Le niveau reste au même niveau.",
      tone: "neutral",
    };
  }

  return {
    label: `${formatSignedInteger(metric.deltaAbsolute)} dossiers`,
    detail:
      metric.deltaRatio === null
        ? "vs période précédente équivalente"
        : `${formatPercent(metric.deltaRatio)} vs période précédente équivalente`,
    tone: comparisonTone(metric.deltaAbsolute, positiveDirection),
  };
}

function buildRateDelta(metric: StatsBoardComparisonMetric): DeltaBadge {
  if (metric.previous === null || metric.deltaAbsolute === null) {
    return {
      label: "",
      detail: "",
      tone: "neutral",
      hidden: true,
    };
  }

  if (metric.deltaAbsolute === 0) {
    return {
      label: "Stable vs période précédente",
      detail: "Le niveau de transformation reste stable.",
      tone: "neutral",
    };
  }

  const prefix = metric.deltaAbsolute > 0 ? "+" : "";

  return {
    label: `${prefix}${formatPercentPoints(metric.deltaAbsolute)}`,
    detail: "vs période précédente équivalente",
    tone: comparisonTone(metric.deltaAbsolute, "up"),
  };
}

function buildBudgetDelta(metric: StatsBoardComparisonMetric): DeltaBadge {
  if (metric.previous === null || metric.deltaAbsolute === null) {
    return {
      label: "",
      detail: "",
      tone: "neutral",
      hidden: true,
    };
  }

  if (metric.deltaAbsolute === 0) {
    return {
      label: "Stable vs période précédente",
      detail: "Le niveau reste au même niveau.",
      tone: "neutral",
    };
  }

  return {
    label: formatSignedCurrency(metric.deltaAbsolute),
    detail:
      metric.deltaRatio === null
        ? "vs période précédente équivalente"
        : `${formatPercent(metric.deltaRatio)} vs période précédente équivalente`,
    tone: comparisonTone(metric.deltaAbsolute, "up"),
  };
}

function buildStaticDelta(label: string, detail: string): DeltaBadge {
  return {
    label,
    detail,
    tone: "neutral",
  };
}

function formatAxisPercent(value: number): string {
  return `${value.toLocaleString("fr-FR", {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  })} %`;
}

function formatAxisDuration(value: number): string {
  if (value < 1) return `${Math.max(1, Math.round(value * 60))} min`;
  return `${value.toLocaleString("fr-FR", {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  })} h`;
}

function getCenterMetricValue(row: StatsBoardCenterRow, metric: CenterMetricKey): number {
  if (metric === "incomingRequests") return row.incomingRequests;
  if (metric === "signedRequests") return row.signedRequests;
  if (metric === "transformationRate") return (row.transformationRate ?? 0) * 100;
  return row.signedBudgetTotal ?? 0;
}

function getCenterMetricLabel(row: StatsBoardCenterRow, metric: CenterMetricKey): string {
  if (metric === "incomingRequests") return formatInteger(row.incomingRequests);
  if (metric === "signedRequests") return formatInteger(row.signedRequests);
  if (metric === "transformationRate") return formatPercent(row.transformationRate);
  return formatCurrency(row.signedBudgetTotal);
}

function formatBoardBase(
  base: StatsBoardMetricBase,
  kind: string,
  periodDescription: string,
): string {
  if (kind === "incomingRequests") {
    return `Base de calcul : ${formatInteger(base.sampleSize ?? 0)} ${pluralize(base.sampleSize ?? 0, "dossier")} créé${(base.sampleSize ?? 0) > 1 ? "s" : ""} sur ${periodDescription}.`;
  }

  if (kind === "signedRequests") {
    return `Base de calcul : ${formatInteger(base.sampleSize ?? 0)} ${pluralize(base.sampleSize ?? 0, "dossier")} considéré${(base.sampleSize ?? 0) > 1 ? "s" : ""} signé${(base.sampleSize ?? 0) > 1 ? "s" : ""} sur ${periodDescription}.`;
  }

  if (kind === "transformationRate") {
    return `Base de calcul : ${formatInteger(base.numerator ?? 0)} dossier${(base.numerator ?? 0) > 1 ? "s" : ""} considéré${(base.numerator ?? 0) > 1 ? "s" : ""} signé${(base.numerator ?? 0) > 1 ? "s" : ""} sur ${formatInteger(base.denominator ?? 0)} demande${(base.denominator ?? 0) > 1 ? "s" : ""} reçue${(base.denominator ?? 0) > 1 ? "s" : ""} sur ${periodDescription}.`;
  }

  return `Base de calcul : ${formatInteger(base.populatedCount ?? 0)} montant${(base.populatedCount ?? 0) > 1 ? "s" : ""} HT exploitable${(base.populatedCount ?? 0) > 1 ? "s" : ""} sur ${formatInteger(base.totalCount ?? 0)} événement${(base.totalCount ?? 0) > 1 ? "s" : ""} confirmé${(base.totalCount ?? 0) > 1 ? "s" : ""} ayant lieu sur ${periodDescription}.`;
}

function formatBpiMonth(value: string): string {
  if (value === BPI_PERIOD_TOTAL_MONTH_START) return "Total période";

  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value || "Mois";

  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function capitalizeBpiExcelLabel(value: string): string {
  return value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function formatBpiExcelMonth(value: string): string {
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value || "Période";

  return capitalizeBpiExcelLabel(
    new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date),
  );
}

function formatBpiExcelPeriodLabel(rows: StatsBpiMonthlyRow[], fallback: string): string {
  const months = [...new Set(rows
    .map((row) => row.monthStart)
    .filter((monthStart) => monthStart !== BPI_PERIOD_TOTAL_MONTH_START))]
    .sort();

  if (months.length === 0) return capitalizeBpiExcelLabel(fallback);
  if (months.length === 1) return formatBpiExcelMonth(months[0]);

  return `${formatBpiExcelMonth(months[0])} → ${formatBpiExcelMonth(months[months.length - 1])}`;
}

function formatBpiExcelFiltersLabel(selectedCenters: string[], clientScope: string[]): string {
  const centersLabel = selectedCenters.length > 0 ? selectedCenters.join(", ") : "Tous les centres";
  const clientTypeLabel = clientScope.length > 0 ? clientScope.join(", ") : "Entreprises/CSE";
  return `Centres : ${centersLabel} · Type client : ${clientTypeLabel}`;
}

function getBpiObjectiveCenterLabel(row: StatsBpiMonthlyRow): string {
  return row.rowKind === "total" ? "__total__" : row.centerLabel;
}

function isBpiPeriodTotalRow(row: StatsBpiMonthlyRow): boolean {
  return row.monthStart === BPI_PERIOD_TOTAL_MONTH_START;
}

function isBpiPhysicalCenterRow(row: StatsBpiMonthlyRow): boolean {
  return row.rowKind === "center" && row.centerLabel.trim().toLowerCase() !== "cse";
}

function parseCurrencyInput(value: string): number | null {
  const normalized = value.replace(/\s/g, "").replace(/,/g, ".");
  if (normalized.length === 0) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parseOptionalCurrencyInput(value: string): { valid: boolean; value: number | null } {
  if (value.trim().length === 0) {
    return { valid: true, value: null };
  }

  const parsedValue = parseCurrencyInput(value);
  return parsedValue === null
    ? { valid: false, value: null }
    : { valid: true, value: parsedValue };
}

function getBpiRowKey(row: StatsBpiMonthlyRow): string {
  return `${row.rowKind}-${row.monthStart}-${row.centerLabel}`;
}

function getBpiActivityRowKey(row: Pick<StatsBpiMonthlyActivityRow, "rowKind" | "monthStart" | "centerLabel">): string {
  return `${row.rowKind}-${row.monthStart}-${row.centerLabel}`;
}

function getEmptyBpiActivityRow(row: StatsBpiMonthlyRow): StatsBpiMonthlyActivityRow {
  return {
    rowKind: row.rowKind,
    monthStart: row.monthStart,
    monthEnd: row.monthEnd,
    monthLabel: row.monthLabel,
    centerLabel: row.centerLabel,
    confirmedEventsCount: 0,
    associationRequests: 0,
    b2bRequests: 0,
    unspecifiedRequests: 0,
    otherRequests: 0,
    totalRequests: 0,
    totalSales: 0,
  };
}

function buildBpiActivityRowMap(
  activityOverview: StatsBpiMonthlyActivityOverview,
): Map<string, StatsBpiMonthlyActivityRow> {
  return new Map(
    activityOverview.rows.map((row) => [getBpiActivityRowKey(row), row]),
  );
}

function getBpiActivityRow(
  row: StatsBpiMonthlyRow,
  activityRows: Map<string, StatsBpiMonthlyActivityRow>,
): StatsBpiMonthlyActivityRow {
  return activityRows.get(getBpiRowKey(row)) ?? getEmptyBpiActivityRow(row);
}

function sumBpiActivityRows(rows: StatsBpiMonthlyActivityRow[]): StatsBpiMonthlyActivityRow {
  return {
    rowKind: "total",
    monthStart: BPI_PERIOD_TOTAL_MONTH_START,
    monthEnd: "",
    monthLabel: "TOTAL PERIODE",
    centerLabel: "Tous centres",
    confirmedEventsCount: rows.reduce((sum, row) => sum + row.confirmedEventsCount, 0),
    associationRequests: rows.reduce((sum, row) => sum + row.associationRequests, 0),
    b2bRequests: rows.reduce((sum, row) => sum + row.b2bRequests, 0),
    unspecifiedRequests: rows.reduce((sum, row) => sum + row.unspecifiedRequests, 0),
    otherRequests: rows.reduce((sum, row) => sum + row.otherRequests, 0),
    totalRequests: rows.reduce((sum, row) => sum + row.totalRequests, 0),
    totalSales: rows.reduce((sum, row) => sum + row.totalSales, 0),
  };
}

function getBpiDisplayActivityRow(
  row: StatsBpiMonthlyRow,
  sourceRows: StatsBpiMonthlyRow[],
  activityRows: Map<string, StatsBpiMonthlyActivityRow>,
): StatsBpiMonthlyActivityRow {
  if (!isBpiPeriodTotalRow(row)) {
    return getBpiActivityRow(row, activityRows);
  }

  return sumBpiActivityRows(
    sourceRows
      .filter((sourceRow) => sourceRow.rowKind === "total")
      .map((sourceRow) => getBpiActivityRow(sourceRow, activityRows)),
  );
}

function getBpiNonB2bRequests(row: StatsBpiMonthlyActivityRow): number {
  return row.totalRequests - row.b2bRequests;
}

function sumNullable(values: Array<number | null>): number | null {
  let hasValue = false;
  const total = values.reduce<number>((sum, value) => {
    if (value === null) return sum;
    hasValue = true;
    return sum + value;
  }, 0);

  return hasValue ? total : null;
}

function computeBpiRate(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null;
  return numerator / denominator;
}

function getBpiTargetValue(
  row: StatsBpiMonthlyRow,
  targetOverrides: Record<string, number | null>,
): number | null {
  const rowKey = getBpiRowKey(row);
  return rowKey in targetOverrides
    ? targetOverrides[rowKey]
    : (row.objectives[BPI_REVENUE_OBJECTIVE_METRIC]?.targetValue ?? null);
}

function getBpiTotalRevenueValue(
  row: StatsBpiMonthlyRow,
  rows: StatsBpiMonthlyRow[],
  totalRevenueOverrides: Record<string, number | null>,
): number | null {
  if (isBpiPeriodTotalRow(row)) return row.totalRevenueHt;

  if (row.rowKind === "center") {
    const rowKey = getBpiRowKey(row);
    return rowKey in totalRevenueOverrides
      ? totalRevenueOverrides[rowKey]
      : row.totalRevenueHt;
  }

  return sumNullable(
    rows
      .filter(
        (candidate) =>
          isBpiPhysicalCenterRow(candidate) && candidate.monthStart === row.monthStart,
      )
      .map((candidate) => getBpiTotalRevenueValue(candidate, rows, totalRevenueOverrides)),
  );
}

function buildBpiDisplayRows(
  rows: StatsBpiMonthlyRow[],
  totalRevenueOverrides: Record<string, number | null>,
  targetOverrides: Record<string, number | null>,
): StatsBpiMonthlyRow[] {
  const monthStarts = [...new Set(rows.map((row) => row.monthStart))];
  if (monthStarts.length <= 1) return rows;

  const monthlyTotalRows = rows.filter((row) => row.rowKind === "total");
  const b2bRevenueHt = sumNullable(monthlyTotalRows.map((row) => row.b2bRevenueHt));
  const totalRevenueHt = sumNullable(
    monthlyTotalRows.map((row) => getBpiTotalRevenueValue(row, rows, totalRevenueOverrides)),
  );
  const quotedCases = monthlyTotalRows.reduce((sum, row) => sum + row.quotedCases, 0);
  const convertedQuotedCases = monthlyTotalRows.reduce(
    (sum, row) => sum + row.convertedQuotedCases,
    0,
  );
  const targetValue = sumNullable(
    monthlyTotalRows.map((row) => getBpiTargetValue(row, targetOverrides)),
  );

  const periodTotalRow: StatsBpiMonthlyRow = {
    rowKind: "total",
    monthStart: BPI_PERIOD_TOTAL_MONTH_START,
    monthEnd: "",
    monthLabel: "TOTAL PERIODE",
    centerLabel: "Tous centres",
    b2bEventsCount: monthlyTotalRows.reduce((sum, row) => sum + row.b2bEventsCount, 0),
    b2bEventsWithAmountCount: monthlyTotalRows.reduce(
      (sum, row) => sum + row.b2bEventsWithAmountCount,
      0,
    ),
    b2bRevenueHt,
    totalRevenueHt,
    b2bRevenueShare: computeBpiRate(b2bRevenueHt, totalRevenueHt),
    quotedCases,
    quotedCasesAbove800: monthlyTotalRows.reduce(
      (sum, row) => sum + row.quotedCasesAbove800,
      0,
    ),
    quotedCasesBelow800: monthlyTotalRows.reduce(
      (sum, row) => sum + row.quotedCasesBelow800,
      0,
    ),
    convertedQuotedCases,
    quoteConversionRate:
      quotedCases === 0 ? null : convertedQuotedCases / quotedCases,
    averageTicketHt: null,
    medianTicketHt: null,
    smallTicketEventsCount: monthlyTotalRows.reduce(
      (sum, row) => sum + row.smallTicketEventsCount,
      0,
    ),
    lowPeriod: false,
    lowPeriodReason: null,
    lowPeriodMedianEventsCount: null,
    objectives:
      targetValue === null
        ? {}
        : {
            [BPI_REVENUE_OBJECTIVE_METRIC]: {
              baselineValue: null,
              targetValue,
              entitlementValue: null,
              notes: null,
            },
          },
  };

  return [periodTotalRow, ...rows];
}

function getBpiObjectiveGap(row: StatsBpiMonthlyRow, targetValue: number | null): number | null {
  if (targetValue === null || row.b2bRevenueHt === null) return null;
  return row.b2bRevenueHt - targetValue;
}

function buildBpiOverviewExportRows(
  rows: StatsBpiMonthlyRow[],
  targetOverrides: Record<string, number | null>,
  totalRevenueOverrides: Record<string, number | null>,
  activityOverview: StatsBpiMonthlyActivityOverview,
  options: { includePeriodTotal?: boolean } = {},
): BpiOverviewExportRow[] {
  const activityRows = buildBpiActivityRowMap(activityOverview);
  const displayRows =
    options.includePeriodTotal === false
      ? rows
      : buildBpiDisplayRows(rows, totalRevenueOverrides, targetOverrides);

  return displayRows.map((row) => {
    const targetValue = getBpiTargetValue(row, targetOverrides);
    const totalRevenueHt = getBpiTotalRevenueValue(row, rows, totalRevenueOverrides);
    const activityRow = getBpiDisplayActivityRow(row, rows, activityRows);

    return {
      month: formatBpiMonth(row.monthStart),
      center: row.centerLabel,
      revenueHt: row.b2bRevenueHt,
      totalRevenueHt,
      revenueShare: computeBpiRate(row.b2bRevenueHt, totalRevenueHt),
      confirmedEventsCount: activityRow.confirmedEventsCount,
      nonB2bRequests: getBpiNonB2bRequests(activityRow),
      b2bRequests: activityRow.b2bRequests,
      totalRequests: activityRow.totalRequests,
      quotedCases: row.quotedCases,
      convertedQuotedCases: row.convertedQuotedCases,
      conversionRate: row.quoteConversionRate,
      targetValue,
      gap: getBpiObjectiveGap(row, targetValue),
    };
  });
}

function buildBpiSummaryExportRows(
  rows: StatsBpiMonthlyRow[],
  targetOverrides: Record<string, number | null>,
  activityOverview: StatsBpiMonthlyActivityOverview,
): BpiOverviewSummaryExportRow[] {
  const activityRows = buildBpiActivityRowMap(activityOverview);

  const buildSummaryRow = (
    center: string,
    sourceRows: StatsBpiMonthlyRow[],
  ): BpiOverviewSummaryExportRow => {
    const revenueHt = sumNullable(sourceRows.map((row) => row.b2bRevenueHt));
    const targetValue = sumNullable(
      sourceRows.map((row) => getBpiTargetValue(row, targetOverrides)),
    );
    const quotedCases = sourceRows.reduce((sum, row) => sum + row.quotedCases, 0);
    const convertedQuotedCases = sourceRows.reduce(
      (sum, row) => sum + row.convertedQuotedCases,
      0,
    );
    const totalRequests = sourceRows.reduce(
      (sum, row) => sum + getBpiActivityRow(row, activityRows).totalRequests,
      0,
    );

    return {
      center,
      revenueHt,
      targetValue,
      gap: revenueHt === null || targetValue === null ? null : revenueHt - targetValue,
      quotedCases,
      convertedQuotedCases,
      conversionRate: quotedCases === 0 ? null : convertedQuotedCases / quotedCases,
      totalRequests,
    };
  };

  const monthlyTotalRows = rows.filter((row) => row.rowKind === "total");
  const centerLabels = [...new Set(rows
    .filter((row) => row.rowKind === "center")
    .map((row) => row.centerLabel))]
    .sort((left, right) => left.localeCompare(right, "fr"));

  return [
    buildSummaryRow("Tous centres", monthlyTotalRows),
    ...centerLabels.map((centerLabel) =>
      buildSummaryRow(
        centerLabel,
        rows.filter((row) => row.rowKind === "center" && row.centerLabel === centerLabel),
      ),
    ),
  ];
}

function buildBpiTotalRevenueSummary(
  summaryRow: BpiOverviewSummaryExportRow,
  rows: StatsBpiMonthlyRow[],
  totalRevenueOverrides: Record<string, number | null>,
): BpiTotalRevenueSummary {
  const monthStarts = [...new Set(rows.map((row) => row.monthStart))].sort();
  const sumRows = (
    sourceRows: StatsBpiMonthlyRow[],
    options: { editable?: boolean; allowPartial?: boolean } = {},
  ): BpiTotalRevenueSummary => {
    const values = sourceRows.map((row) => getBpiTotalRevenueValue(row, rows, totalRevenueOverrides));
    const complete = sourceRows.length > 0 && values.every((value) => value !== null);
    const value = complete || options.allowPartial === true ? sumNullable(values) : null;

    return {
      value,
      complete,
      editableRow: options.editable === true && sourceRows.length === 1 && sourceRows[0].rowKind === "center"
        ? sourceRows[0]
        : null,
    };
  };

  if (summaryRow.center === "Tous centres") {
    return sumRows(rows.filter(isBpiPhysicalCenterRow), { allowPartial: true });
  }

  const sourceRows = monthStarts.map(
    (monthStart) =>
      rows.find(
        (row) =>
          row.rowKind === "center" &&
          row.monthStart === monthStart &&
          row.centerLabel === summaryRow.center,
      ) ?? null,
  );

  if (sourceRows.some((row) => row === null)) {
    return {
      value: null,
      complete: false,
      editableRow: null,
    };
  }

  return sumRows(sourceRows as StatsBpiMonthlyRow[], { editable: true });
}

function formatBpiCsvNumber(value: number | null): string {
  if (value === null) return "";
  return value.toLocaleString("fr-FR", {
    useGrouping: false,
    maximumFractionDigits: 2,
  });
}

function formatBpiCsvPercent(value: number | null): string {
  if (value === null) return "";
  return `${(value * 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

function escapeBpiCsvText(value: string): string {
  const safeValue = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return escapeBpiCsvCell(safeValue);
}

function escapeBpiCsvCell(value: string): string {
  if (
    value.includes('"') ||
    value.includes(BPI_CSV_SEPARATOR) ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildBpiOverviewCsv(
  rows: StatsBpiMonthlyRow[],
  targetOverrides: Record<string, number | null>,
  totalRevenueOverrides: Record<string, number | null>,
  activityOverview: StatsBpiMonthlyActivityOverview,
): string {
  const exportRows = buildBpiOverviewExportRows(
    rows,
    targetOverrides,
    totalRevenueOverrides,
    activityOverview,
  );
  const csvRows = exportRows.map((row) => {
    const cells = [
      escapeBpiCsvText(row.month),
      escapeBpiCsvText(row.center),
      formatBpiCsvNumber(row.revenueHt),
      formatBpiCsvNumber(row.totalRevenueHt),
      escapeBpiCsvCell(formatBpiCsvPercent(row.revenueShare)),
      formatBpiCsvNumber(row.confirmedEventsCount),
      formatBpiCsvNumber(row.nonB2bRequests),
      formatBpiCsvNumber(row.b2bRequests),
      formatBpiCsvNumber(row.totalRequests),
      formatBpiCsvNumber(row.quotedCases),
      formatBpiCsvNumber(row.convertedQuotedCases),
      escapeBpiCsvCell(formatBpiCsvPercent(row.conversionRate)),
      formatBpiCsvNumber(row.targetValue),
      formatBpiCsvNumber(row.gap),
    ];
    return cells.join(BPI_CSV_SEPARATOR);
  });

  return [
    BPI_CSV_HEADERS.map(escapeBpiCsvText).join(BPI_CSV_SEPARATOR),
    ...csvRows,
  ].join("\n");
}

function slugifyBpiCsvFilenamePart(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildBpiOverviewCsvFilename(rows: StatsBpiMonthlyRow[]): string {
  const months = [...new Set(rows.map((row) => row.monthStart))];
  if (months.length !== 1) return "stats-bpi.csv";

  const monthSlug = slugifyBpiCsvFilenamePart(formatBpiMonth(months[0]));
  return monthSlug ? `stats-bpi-${monthSlug}.csv` : "stats-bpi.csv";
}

function buildBpiOverviewExcelFilename(rows: StatsBpiMonthlyRow[]): string {
  const months = [...new Set(rows.map((row) => row.monthStart))]
    .filter((monthStart) => monthStart !== BPI_PERIOD_TOTAL_MONTH_START)
    .sort();

  if (months.length === 0) return "Stats_BPI.xlsx";

  const firstMonth = months[0].slice(0, 7);
  const lastMonth = months[months.length - 1].slice(0, 7);

  return firstMonth === lastMonth
    ? `Stats_BPI_${firstMonth}.xlsx`
    : `Stats_BPI_${firstMonth}_${lastMonth}.xlsx`;
}

function sortBpiRowsForExcelDetail(rows: StatsBpiMonthlyRow[]): StatsBpiMonthlyRow[] {
  return [...rows].sort((left, right) => {
    const monthComparison = left.monthStart.localeCompare(right.monthStart);
    if (monthComparison !== 0) return monthComparison;

    if (left.rowKind !== right.rowKind) {
      return left.rowKind === "total" ? -1 : 1;
    }

    return left.centerLabel.localeCompare(right.centerLabel, "fr");
  });
}

function downloadCsvFile(content: string, filename: string) {
  const blob = new globalThis.Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8" });
  const url = globalThis.URL.createObjectURL(blob);
  const link = globalThis.document.createElement("a");
  link.href = url;
  link.download = filename;
  globalThis.document.body.appendChild(link);
  link.click();
  globalThis.document.body.removeChild(link);
  globalThis.URL.revokeObjectURL(url);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- CSV export code is kept while the BPI UI button is hidden.
function downloadBpiOverviewCsv(
  rows: StatsBpiMonthlyRow[],
  targetOverrides: Record<string, number | null>,
  totalRevenueOverrides: Record<string, number | null>,
  activityOverview: StatsBpiMonthlyActivityOverview,
) {
  downloadCsvFile(
    buildBpiOverviewCsv(rows, targetOverrides, totalRevenueOverrides, activityOverview),
    buildBpiOverviewCsvFilename(rows),
  );
}

function bpiExcelTextCell(value: string, style: Partial<BpiExcelCellStyle> = {}): ExcelCell {
  return {
    value,
    type: String,
    ...style,
  };
}

function bpiExcelUnavailableCell(style: Partial<BpiExcelCellStyle> = {}): ExcelCell {
  return bpiExcelTextCell("N/A", { ...style, ...BPI_EXCEL_UNAVAILABLE_STYLE });
}

function bpiExcelNumberCell(
  value: number | null,
  format: string,
  style: Partial<BpiExcelCellStyle> = {},
): ExcelCell {
  if (value === null) return bpiExcelUnavailableCell(style);

  return {
    value,
    type: Number,
    format,
    ...style,
  };
}

function getBpiExcelGapStyle(value: number | null): Partial<BpiExcelCellStyle> {
  if (value === null) return {};
  if (value === 0) {
    return {
      fontWeight: "bold",
      textColor: "#374151",
    };
  }

  return {
    fontWeight: "bold",
    textColor: value > 0 ? "#15803D" : "#DC2626",
  };
}

function getBpiExcelRowStyle(index: number): Partial<BpiExcelCellStyle> {
  return index % 2 === 0
    ? { ...BPI_EXCEL_BASE_ROW_STYLE, ...BPI_EXCEL_EVEN_ROW_STYLE }
    : BPI_EXCEL_BASE_ROW_STYLE;
}

function getBpiExcelManualStyle(
  rowStyle: Partial<BpiExcelCellStyle>,
  isTotalRow: boolean,
): Partial<BpiExcelCellStyle> {
  return isTotalRow ? rowStyle : { ...rowStyle, ...BPI_EXCEL_MANUAL_STYLE };
}

function getBpiExcelMonthBoundaryStyle(
  rows: BpiOverviewExportRow[],
  index: number,
): Partial<BpiExcelCellStyle> {
  if (index === 0 || rows[index].month === rows[index - 1].month) return {};

  return {
    topBorderColor: "#94A3B8",
    topBorderStyle: "medium",
  };
}

function escapeXmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeXmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function computeBpiAnnualAbsoluteDelta(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null) return null;
  return current - previous;
}

function computeBpiAnnualRatioDelta(
  current: number | null,
  previous: number | null,
): number | null {
  const absoluteDelta = computeBpiAnnualAbsoluteDelta(current, previous);
  if (absoluteDelta === null || previous === null || previous === 0) return null;
  return absoluteDelta / Math.abs(previous);
}

function getBpiExcelMetricFormat(kind: BpiAnnualMetricKind): string {
  if (kind === "currency") return BPI_EXCEL_MONEY_FORMAT;
  if (kind === "percent") return BPI_EXCEL_PERCENT_FORMAT;
  return BPI_EXCEL_INTEGER_FORMAT;
}

function getBpiExcelComparisonDeltaFormat(kind: BpiAnnualMetricKind): string {
  return kind === "percent" ? BPI_EXCEL_PERCENT_FORMAT : getBpiExcelMetricFormat(kind);
}

function hasBpiAnnualPreviousValues(chartRows: BpiAnnualChartRow[]): boolean {
  return chartRows.some((row) => row.previousValue !== null);
}

function buildBpiAnnualComparisonSheetData(
  comparison: BpiAnnualComparisonExportData | null,
  filtersLabel: string,
): SheetData {
  const titleStyle: Partial<BpiExcelCellStyle> = {
    fontWeight: "bold",
    fontSize: 16,
    textColor: BPI_EXCEL_DARK_BLUE,
    height: 30,
  };
  const filtersStyle: Partial<BpiExcelCellStyle> = {
    fontStyle: "italic",
    textColor: "#64748B",
    height: 22,
  };
  const labelStyle: Partial<BpiExcelCellStyle> = {
    ...BPI_EXCEL_BASE_ROW_STYLE,
    fontWeight: "bold",
    backgroundColor: "#F8FAFC",
  };
  const valueStyle: Partial<BpiExcelCellStyle> = {
    ...BPI_EXCEL_BASE_ROW_STYLE,
    align: "right",
  };
  const messageStyle: Partial<BpiExcelCellStyle> = {
    fontStyle: "italic",
    textColor: "#92400E",
    backgroundColor: "#FEF3C7",
    borderColor: "#FBBF24",
    borderStyle: "thin",
  };

  if (!comparison) {
    return [
      [bpiExcelTextCell("Comparaison BPI", { ...titleStyle, columnSpan: 5 })],
      [bpiExcelTextCell(filtersLabel, { ...filtersStyle, columnSpan: 5 })],
      [],
      [bpiExcelTextCell("Comparaison indisponible pour les filtres sélectionnés.", messageStyle)],
    ];
  }

  const { metric, range, chartRows, totals, dataAvailabilityMessage } = comparison;
  const metricFormat = getBpiExcelMetricFormat(metric.kind);
  const deltaFormat = getBpiExcelComparisonDeltaFormat(metric.kind);
  const totalDelta = computeBpiAnnualAbsoluteDelta(totals.current, totals.previous);
  const totalDeltaRatio = computeBpiAnnualRatioDelta(totals.current, totals.previous);
  const missingPreviousMessage =
    !hasBpiAnnualPreviousValues(chartRows)
      ? "Les données N-1 ne sont pas disponibles pour cette période."
      : dataAvailabilityMessage;

  return [
    [
      bpiExcelTextCell(`Comparaison BPI — ${range.selectedLabel}`, {
        ...titleStyle,
        columnSpan: 5,
      }),
    ],
    [bpiExcelTextCell(filtersLabel, { ...filtersStyle, columnSpan: 5 })],
    [],
    [bpiExcelTextCell("Indicateur", labelStyle), bpiExcelTextCell(metric.label, valueStyle)],
    [
      bpiExcelTextCell("Période sélectionnée", labelStyle),
      bpiExcelTextCell(range.selectedLabel, valueStyle),
    ],
    [bpiExcelTextCell("Période N-1", labelStyle), bpiExcelTextCell(range.previousLabel, valueStyle)],
    [
      bpiExcelTextCell("Total période sélectionnée", labelStyle),
      bpiExcelNumberCell(totals.current, metricFormat, valueStyle),
    ],
    [
      bpiExcelTextCell("Total N-1", labelStyle),
      bpiExcelNumberCell(totals.previous, metricFormat, valueStyle),
    ],
    [
      bpiExcelTextCell("Écart en valeur", labelStyle),
      bpiExcelNumberCell(totalDelta, deltaFormat, {
        ...valueStyle,
        ...getBpiExcelGapStyle(totalDelta),
      }),
    ],
    [
      bpiExcelTextCell("Écart en pourcentage", labelStyle),
      bpiExcelNumberCell(totalDeltaRatio, BPI_EXCEL_PERCENT_FORMAT, {
        ...valueStyle,
        ...getBpiExcelGapStyle(totalDeltaRatio),
      }),
    ],
    missingPreviousMessage ? [bpiExcelTextCell(missingPreviousMessage, { ...messageStyle, columnSpan: 5 })] : [],
    [],
    [
      bpiExcelTextCell("Mois", BPI_EXCEL_HEADER_STYLE),
      bpiExcelTextCell(range.currentLegend, BPI_EXCEL_HEADER_STYLE),
      bpiExcelTextCell("N-1", BPI_EXCEL_HEADER_STYLE),
      bpiExcelTextCell("Écart", BPI_EXCEL_HEADER_STYLE),
      bpiExcelTextCell("Écart %", BPI_EXCEL_HEADER_STYLE),
    ],
    ...chartRows.map((row, index) => {
      const rowStyle = getBpiExcelRowStyle(index);
      const numberStyle = { ...rowStyle, align: "right" as const };
      const delta = computeBpiAnnualAbsoluteDelta(row.currentValue, row.previousValue);
      const deltaRatio = computeBpiAnnualRatioDelta(row.currentValue, row.previousValue);

      return [
        bpiExcelTextCell(row.monthLabel, rowStyle),
        bpiExcelNumberCell(row.currentValue, metricFormat, numberStyle),
        bpiExcelNumberCell(row.previousValue, metricFormat, numberStyle),
        bpiExcelNumberCell(delta, deltaFormat, { ...numberStyle, ...getBpiExcelGapStyle(delta) }),
        bpiExcelNumberCell(deltaRatio, BPI_EXCEL_PERCENT_FORMAT, {
          ...numberStyle,
          ...getBpiExcelGapStyle(deltaRatio),
        }),
      ];
    }),
  ];
}

function getBpiExcelComparisonTableStartRow(comparison: BpiAnnualComparisonExportData | null): number {
  if (!comparison) return 0;
  return 13;
}

function createBpiExcelComparisonChartFeature(
  comparison: BpiAnnualComparisonExportData | null,
  sheetIndex: number,
): ExcelFeature<File | Blob | ArrayBuffer> | null {
  if (!comparison || comparison.chartRows.length === 0) return null;

  const tableStartRow = getBpiExcelComparisonTableStartRow(comparison);
  const tableEndRow = tableStartRow + comparison.chartRows.length;
  const hasCurrentValues = comparison.chartRows.some((row) => row.currentValue !== null);
  const hasPreviousValues = hasBpiAnnualPreviousValues(comparison.chartRows);
  if (!hasCurrentValues && !hasPreviousValues) return null;

  const sheetNumber = sheetIndex + 1;
  const drawingRelationshipId = "rId-bpi-comparison-chart";
  const chartRelationshipId = "rId-bpi-comparison-chart-data";
  const chartFileName = `xl/charts/chart${sheetNumber}.xml`;

  const seriesXml = [
    hasCurrentValues
      ? buildBpiExcelChartSeriesXml({
          index: 0,
          order: 0,
          title: comparison.range.currentLegend,
          sheetName: "Comparaison",
          categoryRange: `$A$${tableStartRow + 1}:$A$${tableEndRow}`,
          valueRange: `$B$${tableStartRow + 1}:$B$${tableEndRow}`,
          color: BPI_ANNUAL_CURRENT_COLOR,
        })
      : "",
    hasPreviousValues
      ? buildBpiExcelChartSeriesXml({
          index: 1,
          order: 1,
          title: comparison.range.previousLegend,
          sheetName: "Comparaison",
          categoryRange: `$A$${tableStartRow + 1}:$A$${tableEndRow}`,
          valueRange: `$C$${tableStartRow + 1}:$C$${tableEndRow}`,
          color: BPI_ANNUAL_PREVIOUS_COLOR,
        })
      : "",
  ].join("");

  return {
    files: {
      transform: {
        "[Content_Types].xml": {
          transform: (content) => {
            let nextContent = content;
            if (!nextContent.includes(`/xl/charts/chart${sheetNumber}.xml`)) {
              nextContent = nextContent.replace(
                "</Types>",
                `<Override PartName="/xl/charts/chart${sheetNumber}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/></Types>`,
              );
            }
            if (!nextContent.includes(`/xl/drawings/drawing${sheetNumber}.xml`)) {
              nextContent = nextContent.replace(
                "</Types>",
                `<Override PartName="/xl/drawings/drawing${sheetNumber}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>`,
              );
            }
            return nextContent;
          },
        },
        "xl/worksheets/sheet{id}.xml": {
          transform: (content, _sheetOptions, properties) => {
            if (properties.sheetIndex !== sheetIndex || content.includes(drawingRelationshipId)) {
              return content;
            }

            const withNamespace = content.includes("xmlns:r=")
              ? content
              : content.replace(
                  "<worksheet ",
                  '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ',
                );

            return withNamespace.replace(
              "</worksheet>",
              `<drawing r:id="${drawingRelationshipId}"/></worksheet>`,
            );
          },
        },
        "xl/worksheets/_rels/sheet{id}.xml.rels": {
          insert: (_sheetOptions, properties) => {
            if (properties.sheetIndex !== sheetIndex) return undefined;
            return `<Relationship Id="${drawingRelationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${sheetNumber}.xml"/>`;
          },
        },
        "xl/drawings/drawing{id}.xml": {
          insert: (_sheetOptions, properties) => {
            if (properties.sheetIndex !== sheetIndex) return undefined;
            return buildBpiExcelDrawingXml(chartRelationshipId);
          },
        },
        "xl/drawings/_rels/drawing{id}.xml.rels": {
          insert: (_sheetOptions, properties) => {
            if (properties.sheetIndex !== sheetIndex) return undefined;
            return `<Relationship Id="${chartRelationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart${sheetNumber}.xml"/>`;
          },
        },
      },
      write: {
        files: () => ({
          [chartFileName]: buildBpiExcelChartXml(comparison.metric.label, seriesXml),
        }),
      },
    },
  };
}

function buildBpiExcelFormula(sheetName: string, range: string): string {
  return `'${sheetName.replace(/'/g, "''")}'!${range}`;
}

function buildBpiExcelChartSeriesXml({
  index,
  order,
  title,
  sheetName,
  categoryRange,
  valueRange,
  color,
}: {
  index: number;
  order: number;
  title: string;
  sheetName: string;
  categoryRange: string;
  valueRange: string;
  color: string;
}): string {
  return `
    <c:ser>
      <c:idx val="${index}"/>
      <c:order val="${order}"/>
      <c:tx><c:v>${escapeXmlText(title)}</c:v></c:tx>
      <c:spPr>
        <a:solidFill><a:srgbClr val="${color.replace("#", "")}"/></a:solidFill>
      </c:spPr>
      <c:cat><c:strRef><c:f>${escapeXmlText(buildBpiExcelFormula(sheetName, categoryRange))}</c:f></c:strRef></c:cat>
      <c:val><c:numRef><c:f>${escapeXmlText(buildBpiExcelFormula(sheetName, valueRange))}</c:f></c:numRef></c:val>
    </c:ser>`;
}

function buildBpiExcelChartXml(title: string, seriesXml: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:chart>
    <c:title>
      <c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:r><a:t>${escapeXmlText(title)}</a:t></a:r></a:p></c:rich></c:tx>
      <c:layout/>
    </c:title>
    <c:plotArea>
      <c:layout/>
      <c:barChart>
        <c:barDir val="col"/>
        <c:grouping val="clustered"/>
        ${seriesXml}
        <c:axId val="48650112"/>
        <c:axId val="48672768"/>
      </c:barChart>
      <c:catAx>
        <c:axId val="48650112"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="b"/>
        <c:tickLblPos val="nextTo"/>
        <c:crossAx val="48672768"/>
        <c:crosses val="autoZero"/>
      </c:catAx>
      <c:valAx>
        <c:axId val="48672768"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="l"/>
        <c:majorGridlines/>
        <c:tickLblPos val="nextTo"/>
        <c:crossAx val="48650112"/>
        <c:crosses val="autoZero"/>
      </c:valAx>
    </c:plotArea>
    <c:legend>
      <c:legendPos val="b"/>
      <c:layout/>
    </c:legend>
    <c:plotVisOnly val="1"/>
  </c:chart>
</c:chartSpace>`;
}

function buildBpiExcelDrawingXml(chartRelationshipId: string): string {
  return `
<xdr:twoCellAnchor>
  <xdr:from><xdr:col>6</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>3</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
  <xdr:to><xdr:col>13</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>22</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
  <xdr:graphicFrame macro="">
    <xdr:nvGraphicFramePr>
      <xdr:cNvPr id="2" name="Comparaison BPI"/>
      <xdr:cNvGraphicFramePr/>
    </xdr:nvGraphicFramePr>
    <xdr:xfrm>
      <a:off x="0" y="0"/>
      <a:ext cx="0" cy="0"/>
    </xdr:xfrm>
    <a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
      <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
        <c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="${escapeXmlAttribute(chartRelationshipId)}"/>
      </a:graphicData>
    </a:graphic>
  </xdr:graphicFrame>
  <xdr:clientData/>
</xdr:twoCellAnchor>`;
}

function getExcelColumnName(columnIndex: number): string {
  let index = columnIndex;
  let name = "";

  while (index > 0) {
    const remainder = (index - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    index = Math.floor((index - 1) / 26);
  }

  return name;
}

function buildExcelAutoFilterRef(headerRow: number, columnCount: number, dataRowsCount: number): string {
  const lastColumn = getExcelColumnName(columnCount);
  return `A${headerRow}:${lastColumn}${Math.max(headerRow, headerRow + dataRowsCount)}`;
}

function createBpiExcelAutoFilterFeature(
  refsBySheetIndex: string[],
): ExcelFeature<File | Blob | ArrayBuffer> {
  return {
    files: {
      transform: {
        "xl/worksheets/sheet{id}.xml": {
          transform: (content, _sheetOptions, properties) => {
            const ref = refsBySheetIndex[properties.sheetIndex];
            if (!ref || content.includes("<autoFilter ")) return content;
            return content.replace("</sheetData>", `</sheetData><autoFilter ref="${ref}"/>`);
          },
        },
      },
    },
  };
}

async function downloadBpiOverviewExcel(
  rows: StatsBpiMonthlyRow[],
  targetOverrides: Record<string, number | null>,
  totalRevenueOverrides: Record<string, number | null>,
  activityOverview: StatsBpiMonthlyActivityOverview,
  periodDescription: string,
  selectedCenters: string[],
  clientScope: string[],
  comparison: BpiAnnualComparisonExportData | null,
) {
  const periodLabel = formatBpiExcelPeriodLabel(rows, periodDescription);
  const filtersLabel = formatBpiExcelFiltersLabel(selectedCenters, clientScope);
  const detailSourceRows = sortBpiRowsForExcelDetail(rows);
  const summaryRows = buildBpiSummaryExportRows(
    rows,
    targetOverrides,
    activityOverview,
  );
  const detailRows = buildBpiOverviewExportRows(
    detailSourceRows,
    targetOverrides,
    totalRevenueOverrides,
    activityOverview,
    { includePeriodTotal: false },
  );
  const titleStyle: Partial<BpiExcelCellStyle> = {
    fontWeight: "bold",
    fontSize: 16,
    textColor: BPI_EXCEL_DARK_BLUE,
    height: 30,
  };
  const filtersStyle: Partial<BpiExcelCellStyle> = {
    fontStyle: "italic",
    textColor: "#64748B",
    height: 22,
  };

  const summarySheetData: SheetData = [
    [
      bpiExcelTextCell(`Synthèse BPI — ${periodLabel}`, {
        ...titleStyle,
        columnSpan: BPI_EXCEL_SUMMARY_HEADERS.length,
      }),
    ],
    [
      bpiExcelTextCell(filtersLabel, {
        ...filtersStyle,
        columnSpan: BPI_EXCEL_SUMMARY_HEADERS.length,
      }),
    ],
    [],
    BPI_EXCEL_SUMMARY_HEADERS.map((header) => bpiExcelTextCell(header, BPI_EXCEL_HEADER_STYLE)),
    ...summaryRows.map((row, index) => {
      const isTotalRow = row.center === "Tous centres";
      const rowStyle = isTotalRow ? BPI_EXCEL_TOTAL_STYLE : getBpiExcelRowStyle(index);
      const numberStyle = { ...rowStyle, align: "right" as const };
      const totalRevenueSummary = buildBpiTotalRevenueSummary(
        row,
        rows,
        totalRevenueOverrides,
      );
      const gapStyle = getBpiExcelGapStyle(row.gap);

      return [
        bpiExcelTextCell(row.center, rowStyle),
        bpiExcelNumberCell(row.revenueHt, BPI_EXCEL_MONEY_FORMAT, numberStyle),
        bpiExcelNumberCell(
          totalRevenueSummary.value,
          BPI_EXCEL_MONEY_FORMAT,
          getBpiExcelManualStyle(numberStyle, isTotalRow),
        ),
        bpiExcelNumberCell(
          row.targetValue,
          BPI_EXCEL_MONEY_FORMAT,
          getBpiExcelManualStyle(numberStyle, isTotalRow),
        ),
        bpiExcelNumberCell(row.gap, BPI_EXCEL_MONEY_FORMAT, { ...numberStyle, ...gapStyle }),
        bpiExcelNumberCell(row.quotedCases, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.convertedQuotedCases, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.conversionRate, BPI_EXCEL_PERCENT_FORMAT, numberStyle),
        bpiExcelNumberCell(row.totalRequests, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
      ];
    }),
  ];

  const detailSheetData: SheetData = [
    [
      bpiExcelTextCell(`Détail BPI — ${periodLabel}`, {
        ...titleStyle,
        columnSpan: BPI_CSV_HEADERS.length,
      }),
    ],
    [
      bpiExcelTextCell(filtersLabel, {
        ...filtersStyle,
        columnSpan: BPI_CSV_HEADERS.length,
      }),
    ],
    [],
    BPI_CSV_HEADERS.map((header) => bpiExcelTextCell(header, BPI_EXCEL_HEADER_STYLE)),
    ...detailRows.map((row, index) => {
      const isTotalRow = row.center === "Tous centres";
      const monthBoundaryStyle = getBpiExcelMonthBoundaryStyle(detailRows, index);
      const baseRowStyle = isTotalRow ? BPI_EXCEL_TOTAL_STYLE : getBpiExcelRowStyle(index);
      const rowStyle = { ...baseRowStyle, ...monthBoundaryStyle };
      const numberStyle = { ...rowStyle, align: "right" as const };
      const gapStyle = getBpiExcelGapStyle(row.gap);

      return [
        bpiExcelTextCell(row.month, rowStyle),
        bpiExcelTextCell(row.center, rowStyle),
        bpiExcelNumberCell(row.revenueHt, BPI_EXCEL_MONEY_FORMAT, numberStyle),
        bpiExcelNumberCell(
          row.totalRevenueHt,
          BPI_EXCEL_MONEY_FORMAT,
          getBpiExcelManualStyle(numberStyle, isTotalRow),
        ),
        bpiExcelNumberCell(row.revenueShare, BPI_EXCEL_PERCENT_FORMAT, numberStyle),
        bpiExcelNumberCell(row.confirmedEventsCount, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.nonB2bRequests, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.b2bRequests, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.totalRequests, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.quotedCases, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.convertedQuotedCases, BPI_EXCEL_INTEGER_FORMAT, numberStyle),
        bpiExcelNumberCell(row.conversionRate, BPI_EXCEL_PERCENT_FORMAT, numberStyle),
        bpiExcelNumberCell(
          row.targetValue,
          BPI_EXCEL_MONEY_FORMAT,
          getBpiExcelManualStyle(numberStyle, isTotalRow),
        ),
        bpiExcelNumberCell(row.gap, BPI_EXCEL_MONEY_FORMAT, { ...numberStyle, ...gapStyle }),
      ];
    }),
  ];
  const comparisonSheetData = buildBpiAnnualComparisonSheetData(comparison, filtersLabel);
  const comparisonTableStartRow = getBpiExcelComparisonTableStartRow(comparison);
  const comparisonChartFeature = createBpiExcelComparisonChartFeature(comparison, 2);

  await writeExcelFile(
    [
      {
        data: summarySheetData,
        sheet: "Synthèse",
        stickyRowsCount: BPI_EXCEL_HEADER_ROW,
        stickyColumnsCount: 1,
        showGridLines: false,
        orientation: "landscape",
        columns: [
          { width: 28 },
          { width: 18 },
          { width: 22 },
          { width: 18 },
          { width: 16 },
          { width: 16 },
          { width: 14 },
          { width: 16 },
          { width: 16 },
        ],
      },
      {
        data: detailSheetData,
        sheet: "Détail",
        stickyRowsCount: BPI_EXCEL_HEADER_ROW,
        stickyColumnsCount: 2,
        showGridLines: false,
        orientation: "landscape",
        columns: [
          { width: 15 },
          { width: 28 },
          { width: 18 },
          { width: 20 },
          { width: 16 },
          { width: 14 },
          { width: 16 },
          { width: 24 },
          { width: 14 },
          { width: 14 },
          { width: 14 },
          { width: 18 },
          { width: 18 },
          { width: 16 },
        ],
      },
      {
        data: comparisonSheetData,
        sheet: "Comparaison",
        stickyRowsCount: comparisonTableStartRow > 0 ? comparisonTableStartRow : 4,
        stickyColumnsCount: 1,
        showGridLines: false,
        orientation: "landscape",
        columns: [
          { width: 28 },
          { width: 20 },
          { width: 20 },
          { width: 18 },
          { width: 16 },
          { width: 4 },
          { width: 20 },
          { width: 18 },
          { width: 18 },
          { width: 18 },
          { width: 18 },
          { width: 18 },
          { width: 18 },
        ],
      },
    ],
    {
      features: [
        createBpiExcelAutoFilterFeature([
          buildExcelAutoFilterRef(BPI_EXCEL_HEADER_ROW, BPI_EXCEL_SUMMARY_HEADERS.length, summaryRows.length),
          buildExcelAutoFilterRef(BPI_EXCEL_HEADER_ROW, BPI_CSV_HEADERS.length, detailRows.length),
          comparisonTableStartRow > 0
            ? buildExcelAutoFilterRef(comparisonTableStartRow, 5, comparison?.chartRows.length ?? 0)
            : "",
        ]),
        comparisonChartFeature,
      ].filter((feature): feature is ExcelFeature<File | Blob | ArrayBuffer> => feature !== null),
    },
  ).toFile(buildBpiOverviewExcelFilename(rows));
}

function BpiRevenueObjectiveCell({
  brand,
  row,
  targetValue,
  canEdit,
  onSaved,
}: {
  brand: string;
  row: StatsBpiMonthlyRow;
  targetValue: number | null;
  canEdit: boolean;
  onSaved: (targetValue: number | null) => void;
}) {
  const objective = row.objectives[BPI_REVENUE_OBJECTIVE_METRIC];
  const [draftValue, setDraftValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [targetValue, row.monthStart, row.centerLabel]);

  const startEditing = () => {
    setDraftValue(targetValue === null ? "" : String(targetValue));
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(null);
  };

  if (!canEdit) {
    return targetValue === null ? (
      <span className="text-sm text-gray-500">Non renseigné</span>
    ) : (
      <span className="inline-flex rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-700">
        {formatCurrency(targetValue)}
      </span>
    );
  }

  const saveObjective = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextTarget = parseCurrencyInput(draftValue);
    if (draftValue.trim().length > 0 && nextTarget === null) {
      setError("Montant invalide");
      return;
    }

    setSaving(true);
    setError(null);

    const { error: saveError } = await supabase.from("stats_bpi_objectives_monthly").upsert(
      {
        brand,
        center_label: getBpiObjectiveCenterLabel(row),
        month_start: row.monthStart,
        metric: BPI_REVENUE_OBJECTIVE_METRIC,
        baseline_value: objective?.baselineValue ?? null,
        target_value: nextTarget,
        entitlement_value: objective?.entitlementValue ?? null,
        notes: objective?.notes ?? null,
      },
      { onConflict: "brand,center_label,month_start,metric" },
    );

    setSaving(false);

    if (saveError) {
      setError("Sauvegarde impossible");
      return;
    }

    onSaved(nextTarget);
    setEditing(false);
  };

  if (editing) {
    return (
      <form className="flex min-w-48 flex-col gap-2" onSubmit={saveObjective}>
        <input
          autoFocus
          inputMode="decimal"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          placeholder="Montant HT"
          className="h-9 w-36 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "..." : "Enregistrer"}
          </button>
          <button
            type="button"
            onClick={cancelEditing}
            disabled={saving}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Annuler
          </button>
        </div>
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </form>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={startEditing}
        className={
          targetValue === null
            ? "rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:border-indigo-300 hover:text-indigo-700"
            : "rounded-md border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-800 hover:bg-indigo-100"
        }
      >
        {targetValue === null ? "Renseigner" : formatCurrency(targetValue)}
      </button>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function BpiTotalRevenueCell({
  brand,
  row,
  totalRevenueValue,
  canEdit,
  onSaved,
}: {
  brand: string;
  row: StatsBpiMonthlyRow;
  totalRevenueValue: number | null;
  canEdit: boolean;
  onSaved: (totalRevenueValue: number | null) => void;
}) {
  const [draftValue, setDraftValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [totalRevenueValue, row.monthStart, row.centerLabel]);

  const startEditing = () => {
    setDraftValue(totalRevenueValue === null ? "" : String(totalRevenueValue));
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(null);
  };

  if (!canEdit) {
    return totalRevenueValue === null ? (
      <span className="text-sm text-gray-500">—</span>
    ) : (
      <span className="inline-flex rounded-md border border-gray-200 bg-white/80 px-3 py-1.5 text-sm font-semibold text-gray-700">
        {formatCurrency(totalRevenueValue)}
      </span>
    );
  }

  const saveTotalRevenue = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextTotalRevenue = parseOptionalCurrencyInput(draftValue);
    if (!nextTotalRevenue.valid) {
      setError("Montant invalide");
      return;
    }

    setSaving(true);
    setError(null);

    const { error: saveError } = await supabase.from("stats_bpi_total_revenue_monthly").upsert(
      {
        brand,
        center_label: row.centerLabel,
        month_start: row.monthStart,
        total_revenue_ht: nextTotalRevenue.value,
      },
      { onConflict: "brand,center_label,month_start" },
    );

    setSaving(false);

    if (saveError) {
      setError("Sauvegarde impossible");
      return;
    }

    onSaved(nextTotalRevenue.value);
    setEditing(false);
  };

  if (editing) {
    return (
      <form className="flex min-w-48 flex-col items-end gap-2" onSubmit={saveTotalRevenue}>
        <input
          autoFocus
          inputMode="decimal"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          placeholder="CA global HT"
          className="h-9 w-36 rounded-md border border-gray-300 px-3 text-right text-sm font-medium text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "..." : "Enregistrer"}
          </button>
          <button
            type="button"
            onClick={cancelEditing}
            disabled={saving}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Annuler
          </button>
        </div>
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </form>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={startEditing}
        className={
          totalRevenueValue === null
            ? "rounded-md border border-dashed border-gray-300 bg-white/80 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:border-indigo-300 hover:text-indigo-700"
            : "rounded-md border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-800 hover:bg-indigo-100"
        }
      >
        {totalRevenueValue === null ? "Renseigner" : formatCurrency(totalRevenueValue)}
      </button>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function BpiTotalRevenueSummaryCell({
  brand,
  summary,
  canEdit,
  onSaved,
}: {
  brand: string;
  summary: BpiTotalRevenueSummary;
  canEdit: boolean;
  onSaved: (rowKey: string, totalRevenueValue: number | null) => void;
}) {
  const editableRow = summary.editableRow;
  if (editableRow !== null) {
    return (
      <BpiTotalRevenueCell
        brand={brand}
        row={editableRow}
        totalRevenueValue={summary.value}
        canEdit={canEdit}
        onSaved={(nextTotalRevenueValue) =>
          onSaved(getBpiRowKey(editableRow), nextTotalRevenueValue)
        }
      />
    );
  }

  if (summary.value === null) {
    return (
      <span className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700">
        —
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col items-end rounded-md border border-gray-200 bg-white/80 px-3 py-1.5 text-sm font-semibold text-gray-700">
      <span>{formatCurrency(summary.value)}</span>
      {!summary.complete ? (
        <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600">
          partiel
        </span>
      ) : null}
    </span>
  );
}

function BpiObjectiveGapValue({
  gap,
}: {
  gap: number | null;
}) {
  if (gap === null) {
    return (
      <span className="inline-flex min-w-24 justify-end rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500">
        —
      </span>
    );
  }

  const toneClasses =
    gap > 0
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : gap < 0
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-gray-200 bg-gray-100 text-gray-700";

  return (
    <span className={`inline-flex min-w-24 justify-end rounded-full border px-3 py-1 text-xs font-semibold ${toneClasses}`}>
      {formatSignedCurrency(gap)}
    </span>
  );
}

function formatBpiSummaryPercent(value: number | null): string {
  if (value === null) return "—";
  return `${(value * 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

function normalizeBpiAnnualFilterValues(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((left, right) =>
    left.localeCompare(right, "fr"),
  );
}

function startOfBpiAnnualMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
}

function addBpiAnnualMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 0, 0, 0, 0));
}

function toBpiAnnualMonthValue(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function toBpiAnnualMonthStartKey(date: Date): string {
  return `${toBpiAnnualMonthValue(date)}-01`;
}

function parseBpiAnnualMonthValue(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
}

function capitalizeFirst(value: string): string {
  return value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function formatBpiAnnualMonthLong(date: Date): string {
  return capitalizeFirst(
    new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date),
  );
}

function formatBpiAnnualMonthShort(date: Date): string {
  return capitalizeFirst(
    new Intl.DateTimeFormat("fr-FR", {
      month: "short",
      timeZone: "UTC",
    }).format(date),
  );
}

function formatBpiAnnualPeriodLabel(startMonth: Date, endMonth: Date): string {
  if (toBpiAnnualMonthValue(startMonth) === toBpiAnnualMonthValue(endMonth)) {
    return formatBpiAnnualMonthLong(startMonth);
  }

  return `${formatBpiAnnualMonthLong(startMonth)} → ${formatBpiAnnualMonthLong(endMonth)}`;
}

function buildBpiAnnualMonthOptions(now = new Date()): BpiAnnualMonthOption[] {
  const firstMonth = startOfBpiAnnualMonth(STATS_DATA_START_DATE);
  const currentMonth = startOfBpiAnnualMonth(now);
  const currentYearEnd = new Date(Date.UTC(currentMonth.getUTCFullYear(), 11, 1, 0, 0, 0, 0));
  const lastMonth =
    currentYearEnd.getTime() > currentMonth.getTime() ? currentYearEnd : currentMonth;
  const options: BpiAnnualMonthOption[] = [];

  for (
    let cursor = firstMonth;
    cursor.getTime() <= lastMonth.getTime();
    cursor = addBpiAnnualMonths(cursor, 1)
  ) {
    options.push({
      value: toBpiAnnualMonthValue(cursor),
      label: formatBpiAnnualMonthLong(cursor),
    });
  }

  return options;
}

function getBpiAnnualDefaultMonths(options: BpiAnnualMonthOption[]): {
  startMonth: string;
  endMonth: string;
} {
  const currentMonth = startOfBpiAnnualMonth(new Date());
  const januaryCurrentYear = new Date(Date.UTC(currentMonth.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
  const startMonth = toBpiAnnualMonthValue(januaryCurrentYear);
  const endMonth = toBpiAnnualMonthValue(currentMonth);

  return {
    startMonth: options.some((option) => option.value === startMonth)
      ? startMonth
      : (options[0]?.value ?? endMonth),
    endMonth: options.some((option) => option.value === endMonth)
      ? endMonth
      : (options[options.length - 1]?.value ?? startMonth),
  };
}

function buildBpiAnnualRange(
  startMonthValue: string,
  endMonthValue: string,
): { range: BpiAnnualRange | null; error: string | null } {
  const startMonth = parseBpiAnnualMonthValue(startMonthValue);
  const endMonth = parseBpiAnnualMonthValue(endMonthValue);

  if (!startMonth || !endMonth) {
    return { range: null, error: "Sélection de mois invalide." };
  }

  if (startMonth.getTime() > endMonth.getTime()) {
    return {
      range: null,
      error: "Le mois de début doit être antérieur ou égal au mois de fin.",
    };
  }

  const previousStartMonth = addBpiAnnualMonths(startMonth, -12);
  const previousEndMonth = addBpiAnnualMonths(endMonth, -12);
  const months: BpiAnnualMonthPair[] = [];

  for (
    let cursor = startMonth;
    cursor.getTime() <= endMonth.getTime();
    cursor = addBpiAnnualMonths(cursor, 1)
  ) {
    const previousMonth = addBpiAnnualMonths(cursor, -12);
    months.push({
      currentMonth: cursor,
      previousMonth,
      monthLabel: formatBpiAnnualMonthShort(cursor),
      currentMonthLabel: formatBpiAnnualMonthLong(cursor),
      previousMonthLabel: formatBpiAnnualMonthLong(previousMonth),
    });
  }

  const singleSelectedYear = startMonth.getUTCFullYear() === endMonth.getUTCFullYear();
  const singlePreviousYear =
    previousStartMonth.getUTCFullYear() === previousEndMonth.getUTCFullYear();

  return {
    error: null,
    range: {
      startMonth,
      endMonth,
      fetchFrom: previousStartMonth,
      fetchTo: addBpiAnnualMonths(endMonth, 1),
      selectedLabel: formatBpiAnnualPeriodLabel(startMonth, endMonth),
      previousLabel: formatBpiAnnualPeriodLabel(previousStartMonth, previousEndMonth),
      currentLegend: singleSelectedYear
        ? String(startMonth.getUTCFullYear())
        : "Période sélectionnée",
      previousLegend: singlePreviousYear
        ? String(previousStartMonth.getUTCFullYear())
        : "Période N-1",
      months,
    },
  };
}

function getBpiAnnualMetric(metricKey: BpiAnnualMetricKey): (typeof BPI_ANNUAL_METRICS)[number] {
  return BPI_ANNUAL_METRICS.find((metric) => metric.key === metricKey) ?? BPI_ANNUAL_METRICS[0];
}

function isBpiAnnualClientTypeSelectionSupported(selectedClientTypes: string[]): boolean {
  return (
    selectedClientTypes.length === 0 ||
    (selectedClientTypes.length === 1 && selectedClientTypes[0] === BPI_ANNUAL_CLIENT_TYPE)
  );
}

function getBpiAnnualClientTypeValidation(selectedClientTypes: string[]): string | null {
  if (isBpiAnnualClientTypeSelectionSupported(selectedClientTypes)) return null;

  return "La comparaison annuelle utilise les RPC BPI mensuelles existantes : elle est disponible pour tous les types client ou pour « Une entreprise ou un CSE ». Les autres découpages ne sont pas exposés mois par mois sans modifier la RPC.";
}

function createEmptyBpiAnnualDataState(
  loading = false,
  error: string | null = null,
): BpiAnnualDataState {
  return {
    loading,
    error,
    overview: {
      clientScope: [],
      monthFrom: null,
      monthTo: null,
      rows: [],
    },
    activityOverview: {
      monthFrom: null,
      monthTo: null,
      rows: [],
    },
  };
}

function useBpiAnnualComparisonData({
  brand,
  range,
  selectedCenters,
}: {
  brand: string;
  range: BpiAnnualRange | null;
  selectedCenters: string[];
}): BpiAnnualDataState {
  const [state, setState] = useState<BpiAnnualDataState>(() => createEmptyBpiAnnualDataState());
  const normalizedCenters = useMemo(
    () => normalizeBpiAnnualFilterValues(selectedCenters),
    [selectedCenters],
  );
  const selectedCentersKey = normalizedCenters.join("\u0001");

  useEffect(() => {
    if (!range) {
      setState(createEmptyBpiAnnualDataState());
      return;
    }

    let cancelled = false;

    const fetchAnnualComparison = async () => {
      setState((current) => ({ ...current, loading: true, error: null }));

      const bpiParams = {
        p_brand: brand,
        p_from: range.fetchFrom.toISOString(),
        p_to: range.fetchTo.toISOString(),
        p_centers: normalizedCenters.length > 0 ? normalizedCenters : null,
      };

      const [overviewResult, activityResult] = await Promise.allSettled([
        supabase.rpc("stats_dashboard_bpi_monthly_overview", bpiParams),
        supabase.rpc("stats_dashboard_bpi_monthly_activity_overview", bpiParams),
      ]);

      if (cancelled) return;

      if (overviewResult.status !== "fulfilled" || overviewResult.value.error) {
        setState(
          createEmptyBpiAnnualDataState(
            false,
            buildSectionErrorMessage(
              "Comparaison annuelle",
              overviewResult.status === "fulfilled"
                ? overviewResult.value.error
                : overviewResult.reason,
            ),
          ),
        );
        return;
      }

      if (activityResult.status !== "fulfilled" || activityResult.value.error) {
        setState(
          createEmptyBpiAnnualDataState(
            false,
            buildSectionErrorMessage(
              "Comparaison annuelle",
              activityResult.status === "fulfilled" ? activityResult.value.error : activityResult.reason,
            ),
          ),
        );
        return;
      }

      setState({
        loading: false,
        error: null,
        overview: normalizeBpiOverview(overviewResult.value.data),
        activityOverview: normalizeBpiMonthlyActivityOverview(activityResult.value.data),
      });
    };

    void fetchAnnualComparison();

    return () => {
      cancelled = true;
    };
  }, [brand, range, selectedCentersKey]);

  return state;
}

function buildBpiAnnualOverviewRowMap(rows: StatsBpiMonthlyRow[]): Map<string, StatsBpiMonthlyRow> {
  return new Map(
    rows.filter((row) => row.rowKind === "total").map((row) => [row.monthStart, row]),
  );
}

function buildBpiAnnualActivityRowMap(
  rows: StatsBpiMonthlyActivityRow[],
): Map<string, StatsBpiMonthlyActivityRow> {
  return new Map(
    rows.filter((row) => row.rowKind === "total").map((row) => [row.monthStart, row]),
  );
}

function isBpiAnnualB2bOnly(selectedClientTypes: string[]): boolean {
  return selectedClientTypes.length === 1 && selectedClientTypes[0] === BPI_ANNUAL_CLIENT_TYPE;
}

function getBpiAnnualTotalRequests(
  activityRow: StatsBpiMonthlyActivityRow | null,
  selectedClientTypes: string[],
): number | null {
  if (!activityRow) return null;
  return isBpiAnnualB2bOnly(selectedClientTypes)
    ? activityRow.b2bRequests
    : activityRow.totalRequests;
}

function getBpiAnnualConfirmedEvents(
  overviewRow: StatsBpiMonthlyRow | null,
  activityRow: StatsBpiMonthlyActivityRow | null,
  selectedClientTypes: string[],
): number | null {
  if (!overviewRow && !activityRow) return null;
  return isBpiAnnualB2bOnly(selectedClientTypes)
    ? (overviewRow?.b2bEventsCount ?? 0)
    : (activityRow?.confirmedEventsCount ?? 0);
}

function getBpiAnnualMetricValue(
  metricKey: BpiAnnualMetricKey,
  overviewRow: StatsBpiMonthlyRow | null,
  activityRow: StatsBpiMonthlyActivityRow | null,
  selectedClientTypes: string[],
): number | null {
  if (metricKey === "revenueHt") return overviewRow?.b2bRevenueHt ?? null;
  if (metricKey === "quotedCases") return overviewRow ? overviewRow.quotedCases : null;
  if (metricKey === "convertedQuotedCases") {
    return overviewRow ? overviewRow.convertedQuotedCases : null;
  }
  if (metricKey === "totalRequests") {
    return getBpiAnnualTotalRequests(activityRow, selectedClientTypes);
  }
  if (metricKey === "confirmedEventsCount") {
    return getBpiAnnualConfirmedEvents(overviewRow, activityRow, selectedClientTypes);
  }

  return overviewRow?.quoteConversionRate ?? null;
}

function formatBpiAnnualMetricValue(
  value: number | null,
  kind: BpiAnnualMetricKind,
  options: { compact?: boolean } = {},
): string {
  if (value === null) return "N/A";

  if (kind === "currency") {
    if (options.compact && Math.abs(value) >= 1000) {
      return `${(value / 1000).toLocaleString("fr-FR", {
        maximumFractionDigits: Math.abs(value) >= 10000 ? 0 : 1,
      })} k€`;
    }

    return formatCurrency(value);
  }

  if (kind === "percent") {
    return `${(value * 100).toLocaleString("fr-FR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} %`;
  }

  return formatInteger(Math.round(value));
}

function formatBpiAnnualChartLabel(value: unknown, kind: BpiAnnualMetricKind): string {
  return typeof value === "number" && Number.isFinite(value)
    ? formatBpiAnnualMetricValue(value, kind, { compact: true })
    : "";
}

function formatBpiAnnualAxisValue(value: number, kind: BpiAnnualMetricKind): string {
  if (kind === "currency") {
    if (Math.abs(value) >= 1000) return `${formatInteger(Math.round(value / 1000))} k€`;
    return formatInteger(Math.round(value));
  }

  if (kind === "percent") {
    return `${(value * 100).toLocaleString("fr-FR", {
      maximumFractionDigits: 0,
    })} %`;
  }

  return formatInteger(Math.round(value));
}

function buildBpiAnnualChartRows({
  range,
  overview,
  activityOverview,
  metricKey,
  selectedClientTypes,
}: {
  range: BpiAnnualRange;
  overview: StatsBpiOverview;
  activityOverview: StatsBpiMonthlyActivityOverview;
  metricKey: BpiAnnualMetricKey;
  selectedClientTypes: string[];
}): BpiAnnualChartRow[] {
  const metric = getBpiAnnualMetric(metricKey);
  const overviewRows = buildBpiAnnualOverviewRowMap(overview.rows);
  const activityRows = buildBpiAnnualActivityRowMap(activityOverview.rows);

  return range.months.map((month) => {
    const currentOverviewRow =
      overviewRows.get(toBpiAnnualMonthStartKey(month.currentMonth)) ?? null;
    const previousOverviewRow =
      overviewRows.get(toBpiAnnualMonthStartKey(month.previousMonth)) ?? null;
    const currentActivityRow =
      activityRows.get(toBpiAnnualMonthStartKey(month.currentMonth)) ?? null;
    const previousActivityRow =
      activityRows.get(toBpiAnnualMonthStartKey(month.previousMonth)) ?? null;
    const currentValue = getBpiAnnualMetricValue(
      metricKey,
      currentOverviewRow,
      currentActivityRow,
      selectedClientTypes,
    );
    const previousValue = getBpiAnnualMetricValue(
      metricKey,
      previousOverviewRow,
      previousActivityRow,
      selectedClientTypes,
    );

    return {
      monthLabel: month.monthLabel,
      currentMonthLabel: month.currentMonthLabel,
      previousMonthLabel: month.previousMonthLabel,
      currentValue,
      previousValue,
      currentDisplayValue: formatBpiAnnualMetricValue(currentValue, metric.kind),
      previousDisplayValue: formatBpiAnnualMetricValue(previousValue, metric.kind),
    };
  });
}

function getBpiAnnualPeriodTotal({
  range,
  overview,
  activityOverview,
  metricKey,
  selectedClientTypes,
  period,
}: {
  range: BpiAnnualRange;
  overview: StatsBpiOverview;
  activityOverview: StatsBpiMonthlyActivityOverview;
  metricKey: BpiAnnualMetricKey;
  selectedClientTypes: string[];
  period: "current" | "previous";
}): number | null {
  const overviewRows = buildBpiAnnualOverviewRowMap(overview.rows);
  const activityRows = buildBpiAnnualActivityRowMap(activityOverview.rows);
  const monthDates = range.months.map((month) =>
    period === "current" ? month.currentMonth : month.previousMonth,
  );

  if (metricKey === "conversionRate") {
    const totals = monthDates.reduce(
      (accumulator, monthDate) => {
        const row = overviewRows.get(toBpiAnnualMonthStartKey(monthDate));
        return {
          quotedCases: accumulator.quotedCases + (row?.quotedCases ?? 0),
          convertedQuotedCases:
            accumulator.convertedQuotedCases + (row?.convertedQuotedCases ?? 0),
        };
      },
      { quotedCases: 0, convertedQuotedCases: 0 },
    );

    return computeBpiRate(totals.convertedQuotedCases, totals.quotedCases);
  }

  const values = monthDates.map((monthDate) => {
    const monthKey = toBpiAnnualMonthStartKey(monthDate);
    return getBpiAnnualMetricValue(
      metricKey,
      overviewRows.get(monthKey) ?? null,
      activityRows.get(monthKey) ?? null,
      selectedClientTypes,
    );
  });

  return sumNullable(values);
}

function formatBpiAnnualSignedRatio(value: number): string {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${(value * 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

function formatBpiAnnualSignedPoints(value: number): string {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${(value * 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} pts`;
}

function getBpiAnnualDeltaTone(value: number | null): DeltaTone {
  if (value === null || value === 0) return "neutral";
  return value > 0 ? "positive" : "negative";
}

function buildBpiAnnualTotals({
  range,
  overview,
  activityOverview,
  metricKey,
  selectedClientTypes,
}: {
  range: BpiAnnualRange;
  overview: StatsBpiOverview;
  activityOverview: StatsBpiMonthlyActivityOverview;
  metricKey: BpiAnnualMetricKey;
  selectedClientTypes: string[];
}): BpiAnnualTotals {
  const metric = getBpiAnnualMetric(metricKey);
  const current = getBpiAnnualPeriodTotal({
    range,
    overview,
    activityOverview,
    metricKey,
    selectedClientTypes,
    period: "current",
  });
  const previous = getBpiAnnualPeriodTotal({
    range,
    overview,
    activityOverview,
    metricKey,
    selectedClientTypes,
    period: "previous",
  });

  if (current === null || previous === null) {
    return {
      current,
      previous,
      deltaLabel: "N/A",
      deltaTone: "neutral",
      deltaDetail: "Comparaison N-1 indisponible",
    };
  }

  const absoluteDelta = current - previous;

  if (metric.kind === "percent") {
    return {
      current,
      previous,
      deltaLabel: formatBpiAnnualSignedPoints(absoluteDelta),
      deltaTone: getBpiAnnualDeltaTone(absoluteDelta),
      deltaDetail: "Écart en points vs N-1",
    };
  }

  if (previous === 0) {
    return {
      current,
      previous,
      deltaLabel: current === 0 ? "Stable" : "N/A",
      deltaTone: current === 0 ? "neutral" : "positive",
      deltaDetail: current === 0 ? "Même niveau que N-1" : "Période N-1 à 0",
    };
  }

  return {
    current,
    previous,
    deltaLabel: formatBpiAnnualSignedRatio(absoluteDelta / Math.abs(previous)),
    deltaTone: getBpiAnnualDeltaTone(absoluteDelta),
    deltaDetail: `${formatBpiAnnualMetricValue(absoluteDelta, metric.kind)} vs N-1`,
  };
}

function getBpiAnnualDataAvailabilityMessage(
  range: BpiAnnualRange,
  data: BpiAnnualDataState,
): string | null {
  const monthFrom = data.overview.monthFrom ?? data.activityOverview.monthFrom;
  if (!monthFrom) return null;

  const requestedMonthFrom = toBpiAnnualMonthStartKey(range.fetchFrom);
  if (monthFrom <= requestedMonthFrom) return null;

  const availableDate = new Date(`${monthFrom}T00:00:00.000Z`);
  const availableLabel = Number.isNaN(availableDate.getTime())
    ? monthFrom
    : formatBpiAnnualMonthLong(availableDate);

  return `Les données mensuelles BPI disponibles commencent en ${availableLabel}. Les mois N-1 antérieurs peuvent donc rester vides.`;
}

function BpiAnnualSummaryCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: DeltaTone;
}) {
  const toneClasses =
    tone === "positive"
      ? "text-emerald-700"
      : tone === "negative"
        ? "text-rose-700"
        : "text-gray-900";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-2 text-2xl font-semibold tracking-tight ${toneClasses}`}>{value}</p>
      <p className="mt-1 text-xs text-gray-500">{detail}</p>
    </div>
  );
}

function BpiAnnualComparisonSection({
  monthOptions,
  startMonth,
  endMonth,
  metricKey,
  rangeResult,
  clientTypeValidation,
  annualData,
  metric,
  chartRows,
  totals,
  dataAvailabilityMessage,
  onStartMonthChange,
  onEndMonthChange,
  onMetricKeyChange,
}: {
  monthOptions: BpiAnnualMonthOption[];
  startMonth: string;
  endMonth: string;
  metricKey: BpiAnnualMetricKey;
  rangeResult: { range: BpiAnnualRange | null; error: string | null };
  clientTypeValidation: string | null;
  annualData: BpiAnnualDataState;
  metric: (typeof BPI_ANNUAL_METRICS)[number];
  chartRows: BpiAnnualChartRow[];
  totals: BpiAnnualTotals | null;
  dataAvailabilityMessage: string | null;
  onStartMonthChange: (value: string) => void;
  onEndMonthChange: (value: string) => void;
  onMetricKeyChange: (value: BpiAnnualMetricKey) => void;
}) {
  const effectiveRange = rangeResult.error || clientTypeValidation ? null : rangeResult.range;
  const chartMinWidth = Math.max(760, chartRows.length * 132);
  const hasChartValues = chartRows.some(
    (row) => row.currentValue !== null || row.previousValue !== null,
  );

  return (
    <div>
      <div className="border-b border-gray-200 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Comparaison annuelle</h3>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Comparaison automatique avec la même période N-1.
            </p>
          </div>
          {annualData.loading ? (
            <span className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-700">
              Mise à jour...
            </span>
          ) : null}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <label>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Mois début
            </span>
            <select
              value={startMonth}
              onChange={(event) => onStartMonthChange(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Mois fin
            </span>
            <select
              value={endMonth}
              onChange={(event) => onEndMonthChange(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Indicateur
            </span>
            <select
              value={metricKey}
              onChange={(event) => onMetricKeyChange(event.target.value as BpiAnnualMetricKey)}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {BPI_ANNUAL_METRICS.map((metricOption) => (
                <option key={metricOption.key} value={metricOption.key}>
                  {metricOption.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        {rangeResult.error || clientTypeValidation ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {rangeResult.error ?? clientTypeValidation}
          </div>
        ) : null}

        <SectionError message={annualData.error} />

        {effectiveRange && totals ? (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <BpiAnnualSummaryCard
                label="Total période sélectionnée"
                value={formatBpiAnnualMetricValue(totals.current, metric.kind)}
                detail={effectiveRange.selectedLabel}
              />
              <BpiAnnualSummaryCard
                label="Total période N-1"
                value={formatBpiAnnualMetricValue(totals.previous, metric.kind)}
                detail={effectiveRange.previousLabel}
              />
              <BpiAnnualSummaryCard
                label="Écart"
                value={totals.deltaLabel}
                detail={totals.deltaDetail}
                tone={totals.deltaTone}
              />
            </div>

            {dataAvailabilityMessage ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {dataAvailabilityMessage}
              </div>
            ) : null}

            {!annualData.loading && !annualData.error ? (
              hasChartValues ? (
                <div className="overflow-x-auto">
                  <div className="h-96" style={{ minWidth: `${chartMinWidth}px` }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRows} margin={{ top: 34, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
                        <YAxis
                          allowDecimals={metric.kind !== "integer"}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) =>
                            formatBpiAnnualAxisValue(Number(value), metric.kind)
                          }
                        />
                        <Tooltip
                          formatter={(_value, name, payload) => {
                            const row = payload?.payload as BpiAnnualChartRow | undefined;
                            const displayValue =
                              name === effectiveRange.previousLegend
                                ? row?.previousDisplayValue
                                : row?.currentDisplayValue;
                            return [displayValue ?? "N/A", String(name ?? "")] as [
                              string,
                              string,
                            ];
                          }}
                          labelFormatter={(_label, payload) => {
                            const row = payload?.[0]?.payload as BpiAnnualChartRow | undefined;
                            return row
                              ? `${row.previousMonthLabel} / ${row.currentMonthLabel}`
                              : "Mois";
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="previousValue"
                          name={effectiveRange.previousLegend}
                          fill={BPI_ANNUAL_PREVIOUS_COLOR}
                          radius={[4, 4, 0, 0]}
                        >
                          <LabelList
                            dataKey="previousValue"
                            position="top"
                            formatter={(value: unknown) =>
                              formatBpiAnnualChartLabel(value, metric.kind)
                            }
                          />
                        </Bar>
                        <Bar
                          dataKey="currentValue"
                          name={effectiveRange.currentLegend}
                          fill={BPI_ANNUAL_CURRENT_COLOR}
                          radius={[4, 4, 0, 0]}
                        >
                          <LabelList
                            dataKey="currentValue"
                            position="top"
                            formatter={(value: unknown) =>
                              formatBpiAnnualChartLabel(value, metric.kind)
                            }
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <EmptyState>
                  Aucune donnée mensuelle exploitable pour {metric.label} sur cette comparaison.
                </EmptyState>
              )
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function BpiOverviewTable({
  brand,
  rows,
  activityOverview,
  targetOverrides,
  totalRevenueOverrides,
  canEditObjectives,
  canEditTotalRevenue,
  onTargetSaved,
  onTotalRevenueSaved,
}: {
  brand: string;
  rows: StatsBpiMonthlyRow[];
  activityOverview: StatsBpiMonthlyActivityOverview;
  targetOverrides: Record<string, number | null>;
  totalRevenueOverrides: Record<string, number | null>;
  canEditObjectives: boolean;
  canEditTotalRevenue: boolean;
  onTargetSaved: (rowKey: string, targetValue: number | null) => void;
  onTotalRevenueSaved: (rowKey: string, totalRevenueValue: number | null) => void;
}) {
  const summaryRows = useMemo(
    () => buildBpiSummaryExportRows(rows, targetOverrides, activityOverview),
    [rows, targetOverrides, activityOverview],
  );
  const singleMonthStart = useMemo(() => {
    const monthStarts = [...new Set(rows.map((row) => row.monthStart))];
    return monthStarts.length === 1 ? monthStarts[0] : null;
  }, [rows]);

  const getEditableObjectiveRow = (summaryRow: BpiOverviewSummaryExportRow): StatsBpiMonthlyRow | null => {
    if (singleMonthStart === null) return null;
    if (summaryRow.center === "Tous centres") {
      return rows.find((row) => row.rowKind === "total" && row.monthStart === singleMonthStart) ?? null;
    }

    return rows.find(
      (row) =>
        row.rowKind === "center" &&
        row.monthStart === singleMonthStart &&
        row.centerLabel === summaryRow.center,
    ) ?? null;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] table-fixed border-separate border-spacing-0 text-sm xl:min-w-0">
        <colgroup>
          <col className="w-[11%]" />
          <col className="w-[13%]" />
          <col className="w-[14%]" />
          <col className="w-[12%]" />
          <col className="w-[10%]" />
          <col className="w-[10%]" />
          <col className="w-[10%]" />
          <col className="w-[10%]" />
          <col className="w-[7%]" />
        </colgroup>
        <thead>
          <tr className="bg-[#1E3A5F] text-left text-xs font-bold uppercase tracking-wide text-white">
            <th className="border-b border-[#CBD5E1] px-3 py-4 align-middle">Centre</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">CA HT Smile Event (€)</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">CA HT global centre (€)</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Objectif CA (€)</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Écart (€)</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Devis envoyés</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Devis signés</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Taux de transfo.</th>
            <th className="border-b border-[#CBD5E1] px-3 py-4 text-right align-middle">Total demandes</th>
          </tr>
        </thead>
        <tbody>
          {summaryRows.map((row, index) => {
            const isTotalRow = row.center === "Tous centres";
            const editableObjectiveRow = getEditableObjectiveRow(row);
            const totalRevenueSummary = buildBpiTotalRevenueSummary(
              row,
              rows,
              totalRevenueOverrides,
            );
            const rowClassName = isTotalRow
              ? "bg-blue-50 font-semibold text-gray-950"
              : index % 2 === 0
                ? "bg-gray-50/70 text-gray-900"
                : "bg-white text-gray-900";
            const cellClassName = "border-b border-gray-200 px-3 py-4 align-middle";
            const numericCellClassName = `${cellClassName} text-right tabular-nums`;

            return (
              <tr
                key={row.center}
                className={rowClassName}
              >
                <td className={`${cellClassName} font-semibold`}>{row.center}</td>
                <td className={`${numericCellClassName} font-semibold`}>
                  {formatCurrency(row.revenueHt)}
                </td>
                <td className={numericCellClassName}>
                  <div className="flex justify-end">
                    <BpiTotalRevenueSummaryCell
                      brand={brand}
                      summary={totalRevenueSummary}
                      canEdit={canEditTotalRevenue}
                      onSaved={onTotalRevenueSaved}
                    />
                  </div>
                </td>
                <td className={numericCellClassName}>
                  <div className="flex justify-end">
                    {editableObjectiveRow !== null ? (
                      <BpiRevenueObjectiveCell
                        brand={brand}
                        row={editableObjectiveRow}
                        targetValue={row.targetValue}
                        canEdit={canEditObjectives}
                        onSaved={(nextTargetValue) =>
                          onTargetSaved(getBpiRowKey(editableObjectiveRow), nextTargetValue)
                        }
                      />
                    ) : (
                      <span className="inline-flex rounded-md border border-gray-200 bg-white/80 px-3 py-1.5 text-sm font-semibold text-gray-700">
                        {formatCurrency(row.targetValue)}
                      </span>
                    )}
                  </div>
                </td>
                <td className={numericCellClassName}>
                  <div className="flex justify-end">
                    <BpiObjectiveGapValue gap={row.gap} />
                  </div>
                </td>
                <td className={numericCellClassName}>{formatInteger(row.quotedCases)}</td>
                <td className={numericCellClassName}>
                  {formatInteger(row.convertedQuotedCases)}
                </td>
                <td className={`${numericCellClassName} font-semibold`}>
                  {formatBpiSummaryPercent(row.conversionRate)}
                </td>
                <td className={`${numericCellClassName} font-semibold`}>
                  {formatInteger(row.totalRequests)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      {description ? <p className="mt-2 text-sm leading-6 text-gray-600">{description}</p> : null}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
      <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
      <p className="mt-3 text-sm text-gray-600">Chargement des statistiques...</p>
    </div>
  );
}

function SectionError({ message }: { message: string | null }) {
  if (!message) return null;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
      <p className="text-sm font-medium text-red-800">Section indisponible</p>
      <p className="mt-1 text-sm text-red-700">{message}</p>
    </div>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center">
      <p className="text-sm leading-6 text-gray-600">{children}</p>
    </div>
  );
}

function DeltaPill({ delta, compact = false }: { delta: DeltaBadge; compact?: boolean }) {
  const toneClasses =
    delta.tone === "positive"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : delta.tone === "negative"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-gray-200 bg-gray-100 text-gray-700";

  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClasses}`}>
      <p className={`font-medium ${compact ? "text-xs" : "text-sm"}`}>{delta.label}</p>
      <p className={`mt-1 ${compact ? "text-[11px]" : "text-xs"} opacity-90`}>{delta.detail}</p>
    </div>
  );
}

function ExecutiveCard({
  title,
  value,
  delta,
  description,
  base,
}: {
  title: string;
  value: string;
  delta: DeltaBadge;
  description: string;
  base: string;
}) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-3 break-words text-3xl font-semibold tracking-tight text-gray-900">
            {value}
          </p>
        </div>
        {!delta.hidden ? (
          <div className="max-w-[220px] shrink-0">
            <DeltaPill delta={delta} compact />
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-sm leading-6 text-gray-700">{description}</p>
      <p className="mt-4 text-xs leading-5 text-gray-500">{base}</p>
    </article>
  );
}

function KpiCard({ title, value, note }: { title: string; value: string; note: string }) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-gray-900">{value}</p>
      <p className="mt-4 text-sm leading-6 text-gray-700">{note}</p>
    </article>
  );
}

function downloadMarkdownFile(content: string, filename: string) {
  const blob = new globalThis.Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = globalThis.URL.createObjectURL(blob);
  const link = globalThis.document.createElement("a");
  link.href = url;
  link.download = filename;
  globalThis.document.body.appendChild(link);
  link.click();
  globalThis.document.body.removeChild(link);
  globalThis.URL.revokeObjectURL(url);
}

function StatsServiceNoteBlock({
  loading,
  error,
  officialView,
  available,
  reason,
  generatedAt,
  note,
  markdown,
  exportFilename,
}: {
  loading: boolean;
  error: string | null;
  officialView: boolean;
  available: boolean;
  reason: "ok" | "not_generated_yet" | "unsupported_filters";
  generatedAt: string | null;
  note: StatsServiceNoteNote | null;
  markdown: string | null;
  exportFilename: string;
}) {
  return (
    <section className="space-y-4">
      <SectionHeader
        title="Note de service"
        description="Une lecture écrite, mise à jour hors affichage, pour faire ressortir les écarts et points d'attention les plus utiles."
      />
      <SectionError message={error} />

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-10 shadow-sm">
          <p className="text-sm text-gray-600">Préparation de la note de service...</p>
        </div>
      ) : null}

      {!loading && !error && !officialView && reason === "unsupported_filters" ? (
        <EmptyState>
          Note disponible uniquement sur la vue tous les centres, sans filtre centre ni type
          client.
        </EmptyState>
      ) : null}

      {!loading && !error && officialView && !available ? (
        <EmptyState>La note est en préparation pour cette vue. Elle sera relue puis mise à jour automatiquement.</EmptyState>
      ) : null}

      {!loading && !error && officialView && available && note ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                Synthèse board
              </p>
              <h3 className="mt-2 text-xl font-semibold text-gray-900">{note.headline}</h3>
              {generatedAt ? (
                <p className="mt-2 text-sm text-gray-500">
                  Générée le {formatParisDate(generatedAt)} à {formatParisTime(generatedAt)}
                </p>
              ) : null}
            </div>
            {markdown ? (
              <button
                type="button"
                onClick={() => downloadMarkdownFile(markdown, exportFilename)}
                className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
              >
                Exporter (.md)
              </button>
            ) : null}
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Points clés</h4>
                <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700">
                  {note.executivePoints.map((point) => (
                    <li key={point} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-600" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {note.watchouts.length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Points d&apos;attention</h4>
                  <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700">
                    {note.watchouts.map((point) => (
                      <li key={point} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {note.limitations.length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Limites de lecture</h4>
                  <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700">
                    {note.limitations.map((point) => (
                      <li key={point} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
              <h4 className="text-sm font-semibold text-gray-900">Lecture par centre</h4>
              <div className="mt-4 space-y-4">
                {note.centerReadouts.map((readout) => (
                  <div key={readout.center}>
                    <p className="text-sm font-semibold text-gray-900">{readout.center}</p>
                    <p className="mt-1 text-sm leading-6 text-gray-700">{readout.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function FunnelStepCard({ step }: { step: StatsFunnelStep }) {
  const width = Math.max(step.rateVsInitial ?? 0, step.count > 0 ? 0.06 : 0) * 100;
  const delta = buildCountDelta(
    {
      current: step.count,
      previous: step.previousCount,
      deltaAbsolute: step.deltaAbsolute,
      deltaRatio: step.deltaRatio,
    },
    "up",
  );
  const conversionTone =
    step.conversionToNextRate !== null && step.conversionToNextRate < 0.5
      ? "text-rose-700"
      : "text-gray-900";
  const conversionDescription =
    step.conversionToNextRate === null
      ? "Étape finale du funnel."
      : "Part des dossiers de cette étape qui passent à l'étape suivante.";

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)]">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-gray-900">{step.step}</p>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Parmi les dossiers créés sur la période sélectionnée.
              </p>
            </div>
            <p className="text-2xl font-semibold text-gray-900">{formatInteger(step.count)}</p>
          </div>

          <div className="mt-4 h-3 rounded-full bg-gray-100">
            <div
              className="h-3 rounded-full bg-indigo-600"
              style={{ width: `${Math.min(width, 100)}%` }}
            />
          </div>

          <div className="mt-4 grid gap-3 text-sm text-gray-600 md:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Volume actuel
              </p>
              <p className="mt-1 font-medium text-gray-900">{formatInteger(step.count)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Vers l&apos;étape suivante
              </p>
              <p className={`mt-1 font-medium ${conversionTone}`}>
                {step.conversionToNextRate === null
                  ? "Étape finale"
                  : formatPercent(step.conversionToNextRate)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Depuis les demandes reçues
              </p>
              <p className="mt-1 font-medium text-gray-900">{formatPercent(step.rateVsInitial)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-gray-500">{conversionDescription}</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Comparé à la période précédente équivalente
          </p>
          <div className="mt-3 grid gap-3 text-sm text-gray-700">
            <div className="flex items-center justify-between gap-4">
              <span>Volume précédent</span>
              <span className="font-medium text-gray-900">{formatInteger(step.previousCount)}</span>
            </div>
            <DeltaPill delta={delta} compact />
          </div>
        </div>
      </div>
    </article>
  );
}

function AlertRow({
  count,
  title,
  description,
}: {
  count: number;
  title: string;
  description: string;
}) {
  const hasAlert = count > 0;

  return (
    <div
      className={`rounded-lg border px-4 py-4 ${
        hasAlert ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="mt-1 text-sm leading-6 text-gray-600">{description}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-2xl font-semibold text-gray-900">{formatInteger(count)}</p>
          <p className="text-xs text-gray-500">dossiers concernés</p>
        </div>
      </div>
    </div>
  );
}

function UpcomingWindowCard({
  days,
  eventsCount,
  participantsCount,
}: {
  days: number;
  eventsCount: number;
  participantsCount: number;
}) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-gray-900">Dans les {days} prochains jours</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Événements confirmés
          </p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{formatInteger(eventsCount)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Participants confirmés
          </p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {formatInteger(participantsCount)}
          </p>
        </div>
      </div>
    </article>
  );
}

function StatusPortfolio({ pipeline }: { pipeline: StatsPipeline }) {
  const rows = [...pipeline.statusDistribution].sort((left, right) => {
    return PIPELINE_STATUS_ORDER.indexOf(left.status) - PIPELINE_STATUS_ORDER.indexOf(right.status);
  });
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Répartition des statuts actifs</h3>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            {formatInteger(total)} dossier{total > 1 ? "s" : ""} actif{total > 1 ? "s" : ""} au
            total.
          </p>
        </div>
        <p className="text-xs text-gray-500">
          La barre montre le poids de chaque statut dans le portefeuille du moment.
        </p>
      </div>

      <div className="mt-5 flex h-4 overflow-hidden rounded-full bg-gray-100">
        {rows.map((row) => {
          const share = total === 0 ? 0 : (row.count / total) * 100;
          return (
            <div
              key={row.status}
              className="h-full"
              style={{
                width: `${share}%`,
                backgroundColor: STATUS_COLORS[row.status] ?? "#94a3b8",
              }}
              title={`${row.status}: ${formatInteger(row.count)} dossiers`}
            />
          );
        })}
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <div key={row.status} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[row.status] ?? "#94a3b8" }}
                />
                <span className="text-sm font-medium text-gray-900">{row.status}</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{formatInteger(row.count)}</span>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Âge médian dans le statut : {formatDurationHours(row.medianAgeHours)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeasonalityHeatmap({ cells }: { cells: StatsSeasonalityCell[] }) {
  const days = useMemo(
    () => [
      ...new Map(
        cells.map((cell) => [cell.dayIndex, { dayIndex: cell.dayIndex, dayLabel: cell.dayLabel }]),
      ).values(),
    ],
    [cells],
  );
  const slots = useMemo(
    () => [
      ...new Map(
        cells.map((cell) => [
          cell.hourStart,
          { hourStart: cell.hourStart, slotLabel: cell.slotLabel },
        ]),
      ).values(),
    ],
    [cells],
  );
  const maxValue = cells.reduce((max, cell) => Math.max(max, cell.eventsCount), 0);

  if (days.length === 0 || slots.length === 0) {
    return <EmptyState>Aucune donnée d&apos;horaire sur la période.</EmptyState>;
  }

  const cellByKey = new Map(cells.map((cell) => [`${cell.dayIndex}-${cell.hourStart}`, cell]));

  const backgroundForValue = (value: number) => {
    if (value === 0 || maxValue === 0) return "rgba(226, 232, 240, 0.55)";
    const alpha = 0.22 + (value / maxValue) * 0.58;
    return `rgba(37, 99, 235, ${alpha.toFixed(2)})`;
  };

  const textColorForValue = (value: number) => {
    if (value === 0 || maxValue === 0) return "#475569";
    return value / maxValue > 0.45 ? "#ffffff" : "#0f172a";
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Saisonnalité jour et heure</h3>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Chaque case indique combien d&apos;événements confirmés tombent sur ce créneau, heure de
            Paris.
          </p>
        </div>
        <p className="text-xs text-gray-500">Passe la souris sur une case pour voir le détail.</p>
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-gray-200">
        <div
          className="grid gap-px bg-gray-200"
          style={{ gridTemplateColumns: `112px repeat(${slots.length}, minmax(0, 1fr))` }}
        >
          <div className="bg-white px-3 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Jour
          </div>
          {slots.map((slot) => (
            <div
              key={slot.hourStart}
              className="bg-white px-2 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500"
            >
              {slot.slotLabel}
            </div>
          ))}

          {days.map((day) => (
            <FragmentRow
              key={day.dayIndex}
              day={day.dayLabel}
              slots={slots.map(
                (slot) => cellByKey.get(`${day.dayIndex}-${slot.hourStart}`) ?? null,
              )}
              backgroundForValue={backgroundForValue}
              textColorForValue={textColorForValue}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FragmentRow({
  day,
  slots,
  backgroundForValue,
  textColorForValue,
}: {
  day: string;
  slots: Array<StatsSeasonalityCell | null>;
  backgroundForValue: (value: number) => string;
  textColorForValue: (value: number) => string;
}) {
  return (
    <>
      <div className="bg-white px-3 py-3 text-sm font-medium text-gray-900">{day}</div>
      {slots.map((slot, index) => {
        const value = slot?.eventsCount ?? 0;
        return (
          <div
            key={`${day}-${index}`}
            className="flex h-14 items-center justify-center px-2 text-sm font-semibold"
            style={{
              backgroundColor: backgroundForValue(value),
              color: textColorForValue(value),
            }}
            title={
              slot
                ? `${slot.dayLabel} • ${slot.slotLabel} : ${formatInteger(value)} événement(s)`
                : `${day} : ${formatInteger(value)} événement(s)`
            }
          >
            {formatInteger(value)}
          </div>
        );
      })}
    </>
  );
}

export default function StatsPage() {
  const { brand, brandConfig } = useBrand();
  const { user, loading: authLoading } = useAuth();
  const [rangeKey, setRangeKey] = useState<StatsRangeKey>(() => getCurrentStatsMonthRangeKey());
  const [selectedCenters, setSelectedCenters] = useState<string[]>([]);
  const [selectedClientTypes, setSelectedClientTypes] = useState<string[]>([]);
  const [activeCenterMetric, setActiveCenterMetric] = useState<CenterMetricKey>("incomingRequests");
  const [bpiTargetOverrides, setBpiTargetOverrides] = useState<Record<string, number | null>>({});
  const [bpiTotalRevenueOverrides, setBpiTotalRevenueOverrides] = useState<
    Record<string, number | null>
  >({});
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
  const [activeBpiView, setActiveBpiView] = useState<BpiView>("summary");
  const bpiAnnualMonthOptions = useMemo(() => buildBpiAnnualMonthOptions(), []);
  const bpiAnnualDefaultMonths = useMemo(
    () => getBpiAnnualDefaultMonths(bpiAnnualMonthOptions),
    [bpiAnnualMonthOptions],
  );
  const [bpiAnnualStartMonth, setBpiAnnualStartMonth] = useState(
    bpiAnnualDefaultMonths.startMonth,
  );
  const [bpiAnnualEndMonth, setBpiAnnualEndMonth] = useState(bpiAnnualDefaultMonths.endMonth);
  const [bpiAnnualMetricKey, setBpiAnnualMetricKey] =
    useState<BpiAnnualMetricKey>("revenueHt");

  const {
    loading,
    filters,
    boardSummary,
    boardTrends,
    boardCenters,
    bpiOverview,
    bpiMonthlyActivity,
    eventInsights,
    processOverview,
    funnel,
    pipeline,
    errors,
  } = useStatsDashboard({
    brand,
    rangeKey,
    selectedCenters,
    selectedClientTypes,
  });
  const {
    loading: noteLoading,
    data: serviceNote,
    error: noteError,
  } = useStatsServiceNote({
    brand,
    rangeKey,
    selectedCenters,
    selectedClientTypes,
  });

  useEffect(() => {
    setSelectedCenters((current) => {
      const next = current.filter((center) => filters.centers.includes(center));
      return sameStringArray(current, next) ? current : next;
    });
  }, [filters.centers]);

  useEffect(() => {
    setSelectedClientTypes((current) => {
      const next = current.filter((clientType) => filters.clientTypes.includes(clientType));
      return sameStringArray(current, next) ? current : next;
    });
  }, [filters.clientTypes]);

  useEffect(() => {
    setBpiTargetOverrides({});
    setBpiTotalRevenueOverrides({});
  }, [bpiOverview.rows]);

  useEffect(() => {
    if (authLoading) return;

    if (!user?.id) {
      setCurrentUserRole(null);
      return;
    }

    let cancelled = false;

    const fetchCurrentUserRole = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();

      if (cancelled) return;

      setCurrentUserRole(error ? null : normalizeUserRole(data?.role));
    };

    void fetchCurrentUserRole();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id]);

  const periodDescription = getStatsRangeDescription(rangeKey);
  const eventPeriodDescription =
    rangeKey === "all"
      ? "depuis le 1er novembre 2025, y compris les événements à venir"
      : periodDescription;
  const monthOptions = useMemo(() => getStatsMonthOptions(), []);
  const selectedMonthLabel = formatStatsMonthLabel(rangeKey);
  const normalizedBpiAnnualClientTypes = useMemo(
    () => normalizeBpiAnnualFilterValues(selectedClientTypes),
    [selectedClientTypes],
  );
  const bpiAnnualRangeResult = useMemo(
    () => buildBpiAnnualRange(bpiAnnualStartMonth, bpiAnnualEndMonth),
    [bpiAnnualStartMonth, bpiAnnualEndMonth],
  );
  const bpiAnnualClientTypeValidation = getBpiAnnualClientTypeValidation(
    normalizedBpiAnnualClientTypes,
  );
  const bpiAnnualEffectiveRange =
    bpiAnnualRangeResult.error || bpiAnnualClientTypeValidation
      ? null
      : bpiAnnualRangeResult.range;
  const bpiAnnualData = useBpiAnnualComparisonData({
    brand,
    range: bpiAnnualEffectiveRange,
    selectedCenters,
  });
  const bpiAnnualMetric = getBpiAnnualMetric(bpiAnnualMetricKey);
  const bpiAnnualChartRows = useMemo(
    () =>
      bpiAnnualEffectiveRange
        ? buildBpiAnnualChartRows({
            range: bpiAnnualEffectiveRange,
            overview: bpiAnnualData.overview,
            activityOverview: bpiAnnualData.activityOverview,
            metricKey: bpiAnnualMetricKey,
            selectedClientTypes: normalizedBpiAnnualClientTypes,
          })
        : [],
    [
      bpiAnnualData.activityOverview,
      bpiAnnualData.overview,
      bpiAnnualEffectiveRange,
      bpiAnnualMetricKey,
      normalizedBpiAnnualClientTypes,
    ],
  );
  const bpiAnnualTotals = useMemo(
    () =>
      bpiAnnualEffectiveRange
        ? buildBpiAnnualTotals({
            range: bpiAnnualEffectiveRange,
            overview: bpiAnnualData.overview,
            activityOverview: bpiAnnualData.activityOverview,
            metricKey: bpiAnnualMetricKey,
            selectedClientTypes: normalizedBpiAnnualClientTypes,
          })
        : null,
    [
      bpiAnnualData.activityOverview,
      bpiAnnualData.overview,
      bpiAnnualEffectiveRange,
      bpiAnnualMetricKey,
      normalizedBpiAnnualClientTypes,
    ],
  );
  const bpiAnnualDataAvailabilityMessage =
    bpiAnnualEffectiveRange && !bpiAnnualData.loading && !bpiAnnualData.error
      ? getBpiAnnualDataAvailabilityMessage(bpiAnnualEffectiveRange, bpiAnnualData)
      : null;
  const bpiAnnualComparisonExportData: BpiAnnualComparisonExportData | null =
    bpiAnnualEffectiveRange && bpiAnnualTotals
      ? {
          metric: bpiAnnualMetric,
          range: bpiAnnualEffectiveRange,
          chartRows: bpiAnnualChartRows,
          totals: bpiAnnualTotals,
          dataAvailabilityMessage: bpiAnnualDataAvailabilityMessage,
        }
      : null;

  const boardTrendRows = useMemo(
    () =>
      boardTrends.timeline.map((row) => ({
        ...row,
        transformationRatePct:
          row.transformationRate === null ? null : row.transformationRate * 100,
      })),
    [boardTrends.timeline],
  );

  const processTrendRows = useMemo(
    () =>
      processOverview.timeline.map((row) => ({
        ...row,
      })),
    [processOverview.timeline],
  );

  const participantBandRows = useMemo(
    () => eventInsights.participantBands.map((band: StatsParticipantBand) => ({ ...band })),
    [eventInsights.participantBands],
  );
  const participantsCoverageRate = eventInsights.participantsCoverageRate;
  const hasFirstReplyTrendData = processTrendRows.some(
    (row) => row.meanFirstHumanReplyHours !== null,
  );
  const hasLeadToSignedTrendData = processTrendRows.some(
    (row) => row.meanLeadToSignedHours !== null,
  );
  const showCenterPerformance = selectedCenters.length !== 1;

  const hasSummaryData =
    boardSummary.incomingRequests > 0 ||
    boardSummary.signedRequests > 0 ||
    boardSummary.transformationRate !== null ||
    boardSummary.signedBudgetTotal !== null ||
    boardSummary.signedBudgetMean !== null ||
    boardSummary.signedBudgetMedian !== null ||
    eventInsights.averageParticipantsPerEvent !== null;

  const hasTrendData = boardTrendRows.some(
    (row) =>
      row.incomingRequests > 0 ||
      row.signedRequests > 0 ||
      row.transformationRatePct !== null ||
      row.signedBudgetTotal !== null,
  );

  const hasCenterData = boardCenters.length > 0;
  const hasBpiData = bpiOverview.rows.length > 0;
  const hasEventData = eventInsights.confirmedEventsCount > 0;
  const hasProcessData =
    processOverview.firstHumanReply.sampleSize > 0 ||
    processOverview.leadToSigned.sampleSize > 0;
  const hasFunnelData = funnel.some((step) => step.count > 0);
  const hasPipelineData =
    pipeline.statusDistribution.length > 0 ||
    pipeline.upcomingConfirmed.some((row) => row.eventsCount > 0 || row.participantsCount > 0) ||
    pipeline.urgentBacklog.nonTraiteOver24hWithoutHumanStaff > 0 ||
    pipeline.urgentBacklog.quoteWaitingOver72hWithoutClientReply > 0 ||
    pipeline.urgentBacklog.signedEventWithin7dWithoutDeposit > 0;
  const canEditBpiObjectiveTargets = canManageBpiObjectives(currentUserRole);
  const canEditBpiTotalRevenueValues = canEditBpiTotalRevenue(currentUserRole);

  const isInitialLoading =
    loading &&
    !hasSummaryData &&
    !hasTrendData &&
    !hasCenterData &&
    !hasBpiData &&
    !hasEventData &&
    !hasProcessData &&
    !hasFunnelData &&
    !hasPipelineData &&
    Object.values(errors).every((value) => value === null);

  const executiveCards = [
    {
      title: "Demandes reçues",
      value: formatInteger(boardSummary.incomingRequests),
      delta: buildCountDelta(boardSummary.comparisons.incomingRequests),
      description: "Compte les dossiers créés sur la période, hors dossiers supprimés et imports historiques.",
      base: formatBoardBase(
        boardSummary.bases.incomingRequests,
        "incomingRequests",
        periodDescription,
      ),
    },
    {
      title: "Demandes passées en signé",
      value: formatInteger(boardSummary.signedRequests),
      delta: buildCountDelta(boardSummary.comparisons.signedRequests),
      description:
        "Mesure combien de dossiers ont effectivement atteint le statut Signé sur la période.",
      base: formatBoardBase(boardSummary.bases.signedRequests, "signedRequests", periodDescription),
    },
    {
      title: "Taux de transformation",
      value: formatPercent(boardSummary.transformationRate),
      delta: buildRateDelta(boardSummary.comparisons.transformationRate),
      description:
        "Montre quelle part des demandes reçues sur la période a déjà atteint le statut Signé.",
      base: formatBoardBase(
        boardSummary.bases.transformationRate,
        "transformationRate",
        periodDescription,
      ),
    },
    {
      title: "CA HT moyen événement",
      value: formatCurrency(boardSummary.signedBudgetMean),
      delta: buildBudgetDelta(boardSummary.comparisons.signedBudgetMean),
      description: "Donne le montant HT moyen des événements confirmés de la période, facture finale en priorité puis devis signé.",
      base: formatBoardBase(boardSummary.bases.signedBudgetMean, "signedBudget", periodDescription),
    },
    {
      title: "CA HT médian événement",
      value: formatCurrency(boardSummary.signedBudgetMedian),
      delta: buildBudgetDelta(boardSummary.comparisons.signedBudgetMedian),
      description:
        "Montre le montant HT typique des événements confirmés de la période, sans être tiré par quelques gros événements.",
      base: formatBoardBase(
        boardSummary.bases.signedBudgetMedian,
        "signedBudget",
        periodDescription,
      ),
    },
    {
      title: "CA HT total événement",
      value: formatCurrency(boardSummary.signedBudgetTotal),
      delta: buildBudgetDelta(boardSummary.comparisons.signedBudgetTotal),
      description: "Additionne les montants HT des événements confirmés ayant lieu sur la période, facture finale en priorité puis devis signé.",
      base: formatBoardBase(
        boardSummary.bases.signedBudgetTotal,
        "signedBudget",
        periodDescription,
      ),
    },
    {
      title: "Participants moyens par événement",
      value: formatDecimal(eventInsights.averageParticipantsPerEvent),
      delta: buildStaticDelta(
        "Lecture événementielle",
        "Calculée sur les événements confirmés de la période.",
      ),
      description: "Donne la taille moyenne des groupes déjà confirmés sur la période.",
      base: `Base de calcul : ${formatInteger(eventInsights.participantsKnownEventsCount)} événement${eventInsights.participantsKnownEventsCount > 1 ? "s" : ""} avec participants renseignés sur ${formatInteger(eventInsights.confirmedEventsCount)} événement${eventInsights.confirmedEventsCount > 1 ? "s" : ""} confirmé${eventInsights.confirmedEventsCount > 1 ? "s" : ""}.`,
    },
  ];

  const sortedCenterRows = useMemo(
    () =>
      [...boardCenters].sort((left, right) => {
        const delta =
          getCenterMetricValue(right, activeCenterMetric) -
          getCenterMetricValue(left, activeCenterMetric);
        if (delta !== 0) return delta;
        return left.label.localeCompare(right.label, "fr");
      }),
    [activeCenterMetric, boardCenters],
  );

  const centerChartRows = useMemo(
    () =>
      sortedCenterRows.map((row) => ({
        label: row.label,
        value: getCenterMetricValue(row, activeCenterMetric),
        displayValue: getCenterMetricLabel(row, activeCenterMetric),
      })),
    [activeCenterMetric, sortedCenterRows],
  );

  const centerChartHeight = Math.max(280, centerChartRows.length * 52);

  const backlogAlerts = [
    {
      count: pipeline.urgentBacklog.nonTraiteOver24hWithoutHumanStaff,
      title: "Demandes non reprises depuis plus de 24 h",
      description: "Ces dossiers n'ont reçu aucun premier message humain du staff.",
    },
    {
      count: pipeline.urgentBacklog.quoteWaitingOver72hWithoutClientReply,
      title: "Devis ou relances sans retour client depuis plus de 72 h",
      description: "Le contact est parti, mais aucun retour client n'a encore été observé.",
    },
    {
      count: pipeline.urgentBacklog.signedEventWithin7dWithoutDeposit,
      title: "Signés proches de l'événement sans acompte",
      description:
        "Le dossier est signé, l'événement arrive sous 7 jours, mais l'acompte n'a pas encore été reçu.",
    },
  ];
  const exportFilename = buildStatsServiceNoteFilename({
    brand,
    rangeKey,
    center: selectedCenters.length === 1 ? selectedCenters[0] ?? null : null,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm font-medium text-indigo-700">{brandConfig.name}</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
              Suivi commercial et opérationnel
            </h1>
            <p className="mt-3 text-sm leading-6 text-gray-700">
              Cette page suit quatre choses simples : les demandes reçues, les dossiers passés en
              signé, le CA HT des événements et la vitesse de traitement.
            </p>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Périmètre : demandes des formulaires, ajouts manuels et prospects convertis en dossiers,
              depuis le {CRM_START_LABEL}. Les dossiers supprimés et les imports historiques sont exclus.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <section className="rounded-lg border border-gray-200 bg-white px-6 py-6 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Filtres de lecture</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                La marque reste pilotée par le switch global du header. Les filtres ci-dessous ne
                changent que cette page.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <label className="min-w-[220px]">
                  <span className="sr-only">Mois à afficher</span>
                  <select
                    value={isStatsMonthRangeKey(rangeKey) ? rangeKey : ""}
                    onChange={(event) => {
                      if (!event.target.value) return;
                      setRangeKey(event.target.value as StatsRangeKey);
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      isStatsMonthRangeKey(rangeKey)
                        ? "border-indigo-600 bg-indigo-50 text-indigo-900"
                        : "border-gray-200 bg-white text-gray-700"
                    }`}
                  >
                    <option value="">Mois précis</option>
                    {monthOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {STATS_RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setRangeKey(option.key)}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      option.key === rangeKey
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                {loading ? (
                  <span className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-700">
                    Mise à jour...
                  </span>
                ) : null}
              </div>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                {isStatsMonthRangeKey(rangeKey) && selectedMonthLabel
                  ? `Lecture détaillée sur ${selectedMonthLabel}.`
                  : `Lecture synthétique sur ${periodDescription}.`}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <MultiSelectDropdown
                label="Centres"
                value={selectedCenters}
                onChange={setSelectedCenters}
                options={filters.centers}
                allowCustom={false}
                placeholder="Tous les centres"
              />
              <MultiSelectDropdown
                label="Types client"
                value={selectedClientTypes}
                onChange={setSelectedClientTypes}
                options={filters.clientTypes}
                allowCustom={false}
                placeholder="Tous les types client"
              />
            </div>
          </div>
          <SectionError message={errors.filters} />
        </section>

        {isInitialLoading ? (
          <div className="mt-8">
            <LoadingBlock />
          </div>
        ) : (
          <div className="mt-8 space-y-10">
            <StatsServiceNoteBlock
              loading={noteLoading}
              error={noteError}
              officialView={serviceNote.officialView}
              available={serviceNote.available}
              reason={serviceNote.reason}
              generatedAt={serviceNote.generatedAt}
              note={serviceNote.note}
              markdown={serviceNote.markdown}
              exportFilename={exportFilename}
            />

            <section className="space-y-4">
              <SectionHeader title="Résumé board" />
              <SectionError message={errors.boardSummary} />
              {hasSummaryData ? (
                <div className="grid gap-4 xl:grid-cols-3">
                  {executiveCards.map((card) => (
                    <ExecutiveCard
                      key={card.title}
                      title={card.title}
                      value={card.value}
                      delta={card.delta}
                      description={card.description}
                      base={card.base}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState>Aucune donnée lisible sur {periodDescription}.</EmptyState>
              )}
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Pilotage BPI"
                description="Vue mensuelle CA/devis Entreprises-CSE et demandes tous types, avec les objectifs de CA directement modifiables."
              />
              <SectionError message={errors.bpiOverview} />
              <SectionError message={errors.bpiMonthlyActivity} />

              <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Synthèse mensuelle BPI
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        Vue synthétique pour piloter le CA, les devis et l'écart aux objectifs.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          void downloadBpiOverviewExcel(
                            bpiOverview.rows,
                            bpiTargetOverrides,
                            bpiTotalRevenueOverrides,
                            bpiMonthlyActivity,
                            periodDescription,
                            selectedCenters,
                            bpiOverview.clientScope,
                            bpiAnnualComparisonExportData,
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        Exporter Excel
                      </button>
                      <span className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                        {bpiOverview.clientScope.length > 0
                          ? bpiOverview.clientScope.join(", ")
                          : "Entreprises/CSE"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {[
                      { key: "summary" as const, label: "Synthèse mensuelle BPI" },
                      { key: "comparison" as const, label: "Comparaison" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveBpiView(tab.key)}
                        className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                          activeBpiView === tab.key
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {activeBpiView === "summary" ? (
                  hasBpiData ? (
                    <BpiOverviewTable
                      brand={brand}
                      rows={bpiOverview.rows}
                      activityOverview={bpiMonthlyActivity}
                      targetOverrides={bpiTargetOverrides}
                      totalRevenueOverrides={bpiTotalRevenueOverrides}
                      canEditObjectives={canEditBpiObjectiveTargets}
                      canEditTotalRevenue={canEditBpiTotalRevenueValues}
                      onTargetSaved={(rowKey, nextTargetValue) =>
                        setBpiTargetOverrides((current) => ({
                          ...current,
                          [rowKey]: nextTargetValue,
                        }))
                      }
                      onTotalRevenueSaved={(rowKey, nextTotalRevenueValue) =>
                        setBpiTotalRevenueOverrides((current) => ({
                          ...current,
                          [rowKey]: nextTotalRevenueValue,
                        }))
                      }
                    />
                  ) : (
                    <div className="p-5">
                      <EmptyState>Aucune ligne BPI exploitable sur {periodDescription}.</EmptyState>
                    </div>
                  )
                ) : (
                  <BpiAnnualComparisonSection
                    monthOptions={bpiAnnualMonthOptions}
                    startMonth={bpiAnnualStartMonth}
                    endMonth={bpiAnnualEndMonth}
                    metricKey={bpiAnnualMetricKey}
                    rangeResult={bpiAnnualRangeResult}
                    clientTypeValidation={bpiAnnualClientTypeValidation}
                    annualData={bpiAnnualData}
                    metric={bpiAnnualMetric}
                    chartRows={bpiAnnualChartRows}
                    totals={bpiAnnualTotals}
                    dataAvailabilityMessage={bpiAnnualDataAvailabilityMessage}
                    onStartMonthChange={setBpiAnnualStartMonth}
                    onEndMonthChange={setBpiAnnualEndMonth}
                    onMetricKeyChange={setBpiAnnualMetricKey}
                  />
                )}
              </div>
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Points d'attention"
                description="Les alertes ci-dessous montrent les dossiers qui risquent de ralentir le pipe si rien ne bouge rapidement."
              />
              <SectionError message={errors.pipeline} />
              {hasPipelineData ? (
                <div className="grid gap-4 xl:grid-cols-3">
                  {backlogAlerts.map((alert) => (
                    <AlertRow
                      key={alert.title}
                      count={alert.count}
                      title={alert.title}
                      description={alert.description}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState>Aucun point d&apos;attention à afficher sur la période actuelle.</EmptyState>
              )}
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Funnel commercial"
                description="Le parcours commercial reste cohorté sur les dossiers créés sur la période sélectionnée, avec une comparaison simple vs la période précédente équivalente."
              />
              <SectionError message={errors.funnel} />
              {hasFunnelData ? (
                <div className="space-y-4">
                  {funnel.map((step) => (
                    <FunnelStepCard key={step.step} step={step} />
                  ))}
                </div>
              ) : (
                <EmptyState>
                  Aucun parcours commercial exploitable sur {periodDescription}.
                </EmptyState>
              )}
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Tendances"
                description="Trois vues simples pour lire le rythme des entrées, des signatures et du CA HT des événements sur la période."
              />
              <SectionError message={errors.boardTrends} />
              {hasTrendData ? (
                <div className="grid gap-4 xl:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-900">
                      Demandes reçues vs passées en signé
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Lecture de flux sur {periodDescription}.
                    </p>
                    <div className="mt-5 h-80 min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={boardTrendRows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="bucketLabel" tick={{ fontSize: 12 }} minTickGap={24} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(value, name) =>
                              [formatInteger(Number(value ?? 0)), String(name ?? "")] as [
                                string,
                                string,
                              ]
                            }
                            labelFormatter={(label) => `Période : ${label}`}
                          />
                          <Legend />
                          {BOARD_VOLUME_SERIES.map((series) => (
                            <Line
                              key={series.key}
                              type="monotone"
                              dataKey={series.key}
                              name={series.label}
                              stroke={series.color}
                              strokeWidth={2.5}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-900">
                      Taux de transformation
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Part des demandes reçues sur la période ayant déjà atteint le statut Signé.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      La ligne pointillée rappelle la cible fixée à{" "}
                      {STATS_TARGETS.transformationRatePct}
                      %.
                    </p>
                    <div className="mt-5 h-80 min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={boardTrendRows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="bucketLabel" tick={{ fontSize: 12 }} minTickGap={24} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={formatAxisPercent} />
                          <Tooltip
                            formatter={(value) =>
                              [
                                `${Number(value ?? 0).toLocaleString("fr-FR", {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1,
                                })} %`,
                                "Transformation",
                              ] as [string, string]
                            }
                            labelFormatter={(label) => `Période : ${label}`}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="transformationRatePct"
                            name="Transformation"
                            stroke="#0f766e"
                            strokeWidth={2.5}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            connectNulls
                          />
                          <ReferenceLine
                            y={STATS_TARGETS.transformationRatePct}
                            stroke="#475569"
                            strokeDasharray="6 6"
                            ifOverflow="extendDomain"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-900">CA HT événement total</h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Somme des montants HT des événements confirmés qui ont lieu sur la période, facture finale en priorité.
                    </p>
                    <div className="mt-5 h-80 min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={boardTrendRows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="bucketLabel" tick={{ fontSize: 12 }} minTickGap={24} />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => formatInteger(Number(value))}
                          />
                          <Tooltip
                            formatter={(value) =>
                              [formatCurrency(Number(value ?? 0)), "CA HT événement total"] as [
                                string,
                                string,
                              ]
                            }
                            labelFormatter={(label) => `Période : ${label}`}
                          />
                          <Legend />
                          <Bar
                            dataKey="signedBudgetTotal"
                            name="CA HT événement total"
                            fill="#ea580c"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState>Aucune tendance exploitable sur {periodDescription}.</EmptyState>
              )}
            </section>

            {showCenterPerformance ? (
              <section className="space-y-4">
                <SectionHeader
                  title="Performance par centre"
                  description="Un classement simple pour voir où les demandes entrent, où elles signent et où se concentre le CA HT des événements."
                />
                <SectionError message={errors.boardCenters} />
                {hasCenterData ? (
                  <div className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        {CENTER_METRICS.map((metric) => (
                          <button
                            key={metric.key}
                            type="button"
                            onClick={() => setActiveCenterMetric(metric.key)}
                            className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                              metric.key === activeCenterMetric
                                ? "border-indigo-600 bg-indigo-600 text-white"
                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {metric.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-5 min-w-0" style={{ height: centerChartHeight }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={centerChartRows}
                            layout="vertical"
                            margin={{ left: 16, right: 16 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#e5e7eb"
                              horizontal={false}
                            />
                            <XAxis
                              type="number"
                              tick={{ fontSize: 12 }}
                              tickFormatter={(value) =>
                                activeCenterMetric === "transformationRate"
                                  ? formatAxisPercent(Number(value))
                                  : activeCenterMetric === "signedBudgetTotal"
                                    ? formatInteger(Number(value))
                                    : formatInteger(Number(value))
                              }
                            />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={120}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                              formatter={(value, _name, payload) =>
                                [
                                  payload?.payload?.displayValue ?? String(value ?? ""),
                                  CENTER_METRICS.find((metric) => metric.key === activeCenterMetric)
                                    ?.label ?? "Valeur",
                                ] as [string, string]
                              }
                              labelFormatter={(label) => `Centre : ${label}`}
                            />
                            <Legend />
                            <Bar
                              dataKey="value"
                              name={
                                CENTER_METRICS.find((metric) => metric.key === activeCenterMetric)
                                  ?.label ?? "Valeur"
                              }
                              fill="#2563eb"
                              radius={[0, 6, 6, 0]}
                            >
                              {centerChartRows.map((row) => (
                                <Cell key={row.label} fill="#2563eb" />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                      <div className="border-b border-gray-200 px-5 py-4">
                        <h3 className="text-base font-semibold text-gray-900">Détail par centre</h3>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                          Lecture complète pour retrouver le volume, la transformation et le
                          CA HT des événements.
                        </p>
                      </div>
                      <div className="px-5 py-4">
                        <div className="hidden border-b border-gray-200 pb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 xl:grid xl:grid-cols-[minmax(0,2fr)_0.9fr_0.9fr_1fr_1fr_1fr_1fr] xl:gap-3">
                          <span>Centre</span>
                          <span>Demandes</span>
                          <span>Signées</span>
                          <span>Transformation</span>
                          <span>CA HT événement</span>
                          <span>CA HT moyen</span>
                          <span>CA HT médian</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {sortedCenterRows.map((row) => (
                            <div
                              key={row.label}
                              className="grid gap-3 py-4 text-sm text-gray-700 xl:grid-cols-[minmax(0,2fr)_0.9fr_0.9fr_1fr_1fr_1fr_1fr]"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{row.label}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">Demandes</p>
                                <p>{formatInteger(row.incomingRequests)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">Signées</p>
                                <p>{formatInteger(row.signedRequests)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">Transformation</p>
                                <p>{formatPercent(row.transformationRate)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">CA HT événement</p>
                                <p>{formatCurrency(row.signedBudgetTotal)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">CA HT moyen</p>
                                <p>{formatCurrency(row.signedBudgetMean)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 xl:hidden">CA HT médian</p>
                                <p>{formatCurrency(row.signedBudgetMedian)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState>Aucune comparaison par centre sur {periodDescription}.</EmptyState>
                )}
              </section>
            ) : null}

            <section className="space-y-4">
              <SectionHeader
                title="Événements confirmés"
                description="Une lecture simple du portefeuille confirmé : volume, taille des groupes et moments où les événements tombent."
              />
              <SectionError message={errors.eventInsights} />
              {hasEventData ? (
                <div className="space-y-4">
                  <div className="grid gap-4 xl:grid-cols-2">
                    <KpiCard
                      title="Événements confirmés sur la période"
                      value={formatInteger(eventInsights.confirmedEventsCount)}
                      note={`Base de calcul : dossiers au statut Signé, Acompte reçu ou Versement complet dont la date d'événement tombe sur ${eventPeriodDescription}.`}
                    />
                    <KpiCard
                      title="Nombre moyen de participants"
                      value={formatDecimal(eventInsights.averageParticipantsPerEvent)}
                      note={`Base de calcul : ${formatInteger(eventInsights.participantsKnownEventsCount)} événement${eventInsights.participantsKnownEventsCount > 1 ? "s" : ""} avec un nombre de participants renseigné.`}
                    />
                  </div>

                  <div className="grid gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
                    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                      <h3 className="text-base font-semibold text-gray-900">
                        Répartition des événements par taille
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        Chaque barre montre combien d&apos;événements confirmés tombent dans cette
                        tranche.
                      </p>
                      <div className="mt-5 h-80 min-w-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={participantBandRows}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip
                              formatter={(value) =>
                                [formatInteger(Number(value ?? 0)), "Événements"] as [
                                  string,
                                  string,
                                ]
                              }
                              labelFormatter={(label) => `Tranche : ${label}`}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              name="Événements"
                              fill="#0f766e"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <SeasonalityHeatmap cells={eventInsights.seasonality} />
                  </div>

                  {participantsCoverageRate !== null && participantsCoverageRate < 0.5 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="text-sm text-amber-900">
                        Données participants partielles :{" "}
                        {formatInteger(eventInsights.participantsKnownEventsCount)} événement
                        {eventInsights.participantsKnownEventsCount > 1 ? "s" : ""} sur{" "}
                        {formatInteger(eventInsights.confirmedEventsCount)} renseigné
                        {eventInsights.confirmedEventsCount > 1 ? "s" : ""}. Les chiffres restent
                        visibles, mais ils doivent être lus avec prudence.
                      </p>
                    </div>
                  ) : null}

                  {participantsCoverageRate !== null &&
                  participantsCoverageRate >= 0.5 &&
                  eventInsights.missingParticipantsCount > 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                      <p className="text-sm text-gray-700">
                        {formatInteger(eventInsights.participantsKnownEventsCount)} événement
                        {eventInsights.participantsKnownEventsCount > 1 ? "s" : ""} sur{" "}
                        {formatInteger(eventInsights.confirmedEventsCount)} ont déjà un nombre de
                        participants renseigné.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <EmptyState>Aucun événement confirmé daté sur {periodDescription}.</EmptyState>
              )}
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Suivi process"
                description="Une lecture simple du temps moyen de prise en charge et du temps moyen nécessaire pour faire passer une demande en signé."
              />
              <SectionError message={errors.process} />
              {hasProcessData ? (
                <div className="space-y-4">
                  <div className="grid gap-4 xl:grid-cols-2">
                    <KpiCard
                      title="Temps moyen avant première réponse humaine"
                      value={formatDurationHours(processOverview.firstHumanReply.meanHours)}
                      note={`Médiane : ${formatDurationHours(processOverview.firstHumanReply.medianHours)}. 90 % des dossiers traités plus lentement restent sous ${formatDurationHours(processOverview.firstHumanReply.p90Hours)}. Base : ${formatInteger(processOverview.firstHumanReply.sampleSize)} dossier${processOverview.firstHumanReply.sampleSize > 1 ? "s" : ""} avec une première réponse humaine.`}
                    />
                    <KpiCard
                      title='Temps moyen entre demande et "Signé"'
                      value={formatDurationHours(processOverview.leadToSigned.meanHours)}
                      note={`Médiane : ${formatDurationHours(processOverview.leadToSigned.medianHours)}. 90 % des dossiers passés en signé plus lentement restent sous ${formatDurationHours(processOverview.leadToSigned.p90Hours)}. Base : ${formatInteger(processOverview.leadToSigned.sampleSize)} dossier${processOverview.leadToSigned.sampleSize > 1 ? "s" : ""} passés en signé.`}
                    />
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                      <h3 className="text-base font-semibold text-gray-900">
                        Évolution du temps de première réponse
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        Les courbes sont basées sur les dossiers créés sur {periodDescription}.
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        La ligne pointillée rappelle la cible fixée à{" "}
                        {formatDurationHours(STATS_TARGETS.firstReplyHours)}.
                      </p>
                      {hasFirstReplyTrendData ? (
                        <div className="mt-5 h-80 min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={processTrendRows}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis
                                dataKey="bucketLabel"
                                tick={{ fontSize: 12 }}
                                minTickGap={24}
                              />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={formatAxisDuration} />
                              <Tooltip
                                formatter={(value) =>
                                  [formatDurationHours(Number(value ?? 0)), "Temps moyen"] as [
                                    string,
                                    string,
                                  ]
                                }
                                labelFormatter={(label) => `Période : ${label}`}
                              />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="meanFirstHumanReplyHours"
                                name="Temps moyen"
                                stroke="#2563eb"
                                strokeWidth={2.5}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls
                              />
                              <ReferenceLine
                                y={STATS_TARGETS.firstReplyHours}
                                stroke="#475569"
                                strokeDasharray="6 6"
                                ifOverflow="extendDomain"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="mt-5 text-sm text-gray-600">
                          Aucune série de délai de réponse sur {periodDescription}.
                        </p>
                      )}
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                      <h3 className="text-base font-semibold text-gray-900">
                        Évolution du temps entre demande et signé
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        Les courbes sont basées sur les dossiers créés sur {periodDescription}.
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        La ligne pointillée rappelle la cible fixée à{" "}
                        {formatDurationHours(STATS_TARGETS.leadToSignedHours)}.
                      </p>
                      {hasLeadToSignedTrendData ? (
                        <div className="mt-5 h-80 min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={processTrendRows}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis
                                dataKey="bucketLabel"
                                tick={{ fontSize: 12 }}
                                minTickGap={24}
                              />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={formatAxisDuration} />
                              <Tooltip
                                formatter={(value) =>
                                  [formatDurationHours(Number(value ?? 0)), "Temps moyen"] as [
                                    string,
                                    string,
                                  ]
                                }
                                labelFormatter={(label) => `Période : ${label}`}
                              />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="meanLeadToSignedHours"
                                name="Temps moyen"
                                stroke="#ea580c"
                                strokeWidth={2.5}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls
                              />
                              <ReferenceLine
                                y={STATS_TARGETS.leadToSignedHours}
                                stroke="#475569"
                                strokeDasharray="6 6"
                                ifOverflow="extendDomain"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="mt-5 text-sm text-gray-600">
                          Aucune série de passage en signé sur {periodDescription}.
                        </p>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                <EmptyState>Aucune mesure process exploitable sur {periodDescription}.</EmptyState>
              )}
            </section>

            <section className="space-y-4">
              <SectionHeader
                title="Portefeuille actuel"
                description="Cette partie garde une lecture utile du portefeuille actif et de ce qui est déjà confirmé pour les prochains jours."
              />
              <SectionError message={errors.pipeline} />
              {hasPipelineData ? (
                <div className="space-y-4">
                  <StatusPortfolio pipeline={pipeline} />

                  <div className="grid gap-4 md:grid-cols-3">
                    {pipeline.upcomingConfirmed.map((window) => (
                      <UpcomingWindowCard
                        key={window.days}
                        days={window.days}
                        eventsCount={window.eventsCount}
                        participantsCount={window.participantsCount}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState>Aucun portefeuille actif lisible en ce moment.</EmptyState>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
